class TactTheme {
  constructor() {
    this.header = document.querySelector("[data-site-header]");
    this.menu = document.querySelector("[data-menu]");
    this.overlay = document.querySelector("[data-overlay]");
    this.sizeGuide = document.querySelector("[data-size-guide]");
    this.sizeOverlay = document.querySelector(".t-size-overlay");
    this.cartDrawer = document.querySelector("[data-cart-drawer]");
    this.cartOverlay = document.querySelector(".t-cart-drawer-overlay");
    /* Read by anyOverlayOpen() and releaseStuckScrollLock(). Neither ever set
       it, so both treated an open wishlist as nothing at all: the dead-man's
       handle runs on every click, found no panel on screen, and tore the
       scroll lock off while the shopper was still using the drawer. */
    this.wishlist = document.querySelector("[data-wishlist-drawer]");
    this.mobileDock = document.querySelector("[data-mobile-dock]");
    this.dockIdleTimer = null;
    this.designMode =
      window.Shopify?.designMode === true ||
      document.documentElement.dataset.designMode === "true";
    this.lastScrollY = window.scrollY;
    this.setupMotion();
    this.bind();
    this.bindStoryVideos();
    this.bindCategoryDecks();
    this.bindCollectionFilters();
    this.bindHeroCarousels();
    this.bindHeroVideos();
    this.bindReviewStories();
    this.bindVideoQuickAdd();
    this.bindProductForms();
    this.bindVariantOptions();
    this.bindNavDrops();
    this.bindMenuCategories();
    this.bindDragRails();
    this.bindCartOffers();
    this.bindCouponField();
    this.bindFooterMenus();
    this.bindImageReveal();
    this.bindProductMediaGallery();
    this.bindProductPairsInset();
    this.bindProductLightbox();
    this.bindProductPurchaseLayout();
    this.bindProductBuybox();
    this.bindCartDrawer();
    this.bindWishlist();
    this.bindQuickView();
    this.bindProductCardMotion();
    this.bindQuickGalleries();
    this.bindProductStickyBar();
    this.bindVideoLook();
    this.bindHeaderScramble();
    this.bindHeaderMarker();
    this.bindRevealMotion();
    this.bindHeroIntro();
    this.bindThemeMode();
    this.bindMobileDock();
    this.bindAccountSheet();
    this.bindSearch();
    this.bindCheckoutTracking();
    this.bindCartPageRedirect();
    this.bindScrollLockGuard();
    this.ensureCheckoutClickable();
    this.hydrateCart();
    this.onScroll();
  }

  /* --------------------------------------------------------------------------
     Motion kit. A thin wrapper over the vendored Motion One build so drawers,
     the add button and reveals get spring physics instead of linear CSS
     transitions. Every helper degrades to an instant final state when Motion
     is absent or the visitor asked for reduced motion, and the CSS class
     fallbacks stay intact, so nothing here is load-bearing.
     -------------------------------------------------------------------------- */
  setupMotion() {
    const root = document.documentElement;
    const enabled = root.dataset.motion !== "off";
    const honorReduce = root.dataset.reduceMotion !== "ignore";
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reduceMotion = honorReduce && query.matches;
    query.addEventListener?.("change", (event) => {
      this.reduceMotion = honorReduce && event.matches;
      this.motionOk = Boolean(this.M) && !this.reduceMotion;
      root.classList.toggle("t-motion-js", this.motionOk);
    });
    this.M = enabled && window.Motion ? window.Motion : null;
    this.motionOk = Boolean(this.M) && !this.reduceMotion;
    this.spring = {
      snap: { type: "spring", stiffness: 520, damping: 34, mass: 0.9 },
      sheet: { type: "spring", stiffness: 380, damping: 40, mass: 1 },
      soft: { type: "spring", stiffness: 240, damping: 26 },
    };
    if (this.motionOk) root.classList.add("t-motion-js");
  }

  /* Spring a value set onto an element; resolve immediately when motion is off. */
  animateSpring(el, keyframes, preset = "sheet", extra = {}) {
    if (!el) return Promise.resolve();
    if (!this.motionOk) {
      Object.entries(keyframes).forEach(([key, value]) => {
        const end = Array.isArray(value) ? value[value.length - 1] : value;
        if (key === "opacity") el.style.opacity = String(end);
        else if (key === "x") el.style.transform = `translateX(${end})`;
        else if (key === "y") el.style.transform = `translateY(${end})`;
        else if (key === "scale") el.style.transform = `scale(${end})`;
      });
      return Promise.resolve();
    }
    const animation = this.M.animate(el, keyframes, { ...this.spring[preset], ...extra });
    return animation.finished ? animation.finished.catch(() => {}) : Promise.resolve();
  }

  /* Slide a drawer panel in/out along its natural axis. `closed` is the panel's
     resting transform offset (matches the CSS default). `preset` picks the
     spring — the top menu uses `snap` so it settles fast; a control that is
     still travelling under the cursor is what makes a close button feel like it
     is dodging the click. */
  animateDrawer(panel, scrim, open, { axis = "x", closed = "104%", preset = "sheet" } = {}) {
    if (!this.motionOk || !panel) return;
    const base = this.spring[preset] || this.spring.sheet;
    if (scrim) {
      this.M.animate(
        scrim,
        { opacity: open ? [0, 1] : 0 },
        { duration: open ? 0.24 : 0.18, easing: open ? "ease-out" : "ease-in" },
      );
    }
    this.M.animate(
      panel,
      { [axis]: open ? [closed, "0%"] : closed },
      open ? base : { ...base, stiffness: base.stiffness + 90, damping: base.damping + 6 },
    );
  }

  press(el, scale = 0.94) {
    if (!this.motionOk || !el) return;
    /* Some controls are positioned with a CSS transform (e.g. the menu close
       button is translateY(-50%) centred). Animating `scale` in isolation
       clobbers that transform and the control jumps, so compose the press onto
       whatever transform the element already carries. */
    const base = getComputedStyle(el).transform;
    if (base && base !== "none") {
      this.M.animate(
        el,
        { transform: [base, `${base} scale(${scale})`, base] },
        { duration: 0.32, easing: [0.34, 1.56, 0.64, 1] },
      );
      return;
    }
    this.M.animate(el, { scale: [1, scale, 1] }, { duration: 0.32, easing: [0.34, 1.56, 0.64, 1] });
  }

  /* One quiet entrance for the hero copy on first paint. No parallax: the hero
     carousel already owns the slide transforms. */
  bindHeroIntro() {
    if (!this.motionOk || this.heroIntroDone) return;
    const copy = document.querySelector(".t-hero-slide.is-active .t-hero-slide__copy");
    if (!copy) return;
    this.heroIntroDone = true;
    const parts = copy.querySelectorAll(":scope > *");
    if (!parts.length) return;
    this.M.animate(parts, { opacity: [0, 1], y: [14, 0] }, { ...this.spring.soft, delay: this.M.stagger(0.06, { start: 0.1 }) });
  }

  shopifyPath(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root}${String(path || "").replace(/^\/+/, "")}`;
  }

  async requestJson(path, options = {}, timeoutMs = 10000) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(this.shopifyPath(path), {
        ...options,
        headers: { Accept: "application/json", ...options.headers },
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const error = new Error(payload?.description || payload?.message || "The request could not be completed.");
        error.status = response.status;
        throw error;
      }
      return payload;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  safeStoreUrl(value, fallback = "#") {
    try {
      const url = new URL(String(value || ""), window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return fallback;
    }
  }

  safeImageUrl(value) {
    try {
      const url = new URL(String(value || ""), window.location.origin);
      if (url.protocol !== "https:" && url.origin !== window.location.origin) return "";
      return url.href;
    } catch {
      return "";
    }
  }

  normalizeWishlistItem(candidate) {
    if (!candidate || typeof candidate !== "object") return null;
    const handle = String(candidate.handle || "").trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return null;
    return {
      handle,
      title: String(candidate.title || "").trim().slice(0, 160),
      price: String(candidate.price || "").trim().slice(0, 48),
      url: this.safeStoreUrl(candidate.url, `/products/${handle}`),
      image: this.safeImageUrl(candidate.image),
    };
  }

  bind() {
    window.addEventListener("scroll", () => this.onScroll(), { passive: true });

    document.querySelectorAll("[data-menu-open]").forEach((button) => {
      button.addEventListener("click", () => this.openMenu());
    });
    document.querySelectorAll("[data-menu-close], [data-overlay]").forEach((button) => {
      button.addEventListener("click", () => this.closeMenu());
    });
    /* Delegated, not bound per element. The quick-add drawer's markup is
       fetched and injected after boot, so its own Sizing guide button was never
       wired up and the click did nothing at all. */
    document.addEventListener("click", (event) => {
      const guideTrigger = event.target.closest("[data-size-guide-open]");
      if (guideTrigger) {
        event.preventDefault();
        /* On a product page the section's own panel is already correct. */
        if (document.querySelector("[data-size-guide]")) {
          this.openSizeGuide(guideTrigger.dataset.sizePart);
          return;
        }
        /* Anywhere else — the quick-add drawer — fetch the product's chart. */
        const root = guideTrigger.closest("[data-quick-view-root]")
          || document.querySelector("[data-quick-view-root]");
        this.openSharedSizeGuide(
          root?.dataset.productUrl,
          root?.querySelector(".qv__title, h1, h2")?.textContent?.trim(),
        );
        return;
      }
      if (event.target.closest("[data-size-guide-close]")) this.closeSizeGuide();
    });
    /* The product page's rails are handled by bindVariantOptions, which knows
       about combinations. This stays for the quick-add drawer, whose radios
       still carry a variant id and its price. */
    document.querySelectorAll(".t-variant-list input[name='id']").forEach((input) => {
      input.addEventListener("change", (event) => this.updateVariant(event.target));
    });
    /* Capture phase, so nothing between the button and the document can
       swallow it, and the event stops here so no other delegated handler acts
       on the same click. The tabs sit inside a fixed panel that also carries a
       close-on-click overlay; that is a stack worth being explicit in. */
    document.addEventListener(
      "click",
      (event) => {
        const tab = event.target.closest("[data-chart-tab]");
        if (!tab) return;
        event.preventDefault();
        event.stopPropagation();
        this.showChartTab(tab);
      },
      true,
    );
    document.querySelectorAll("[data-quantity-minus]").forEach((button) => {
      button.addEventListener("click", () => this.changeQuantity(button, -1));
    });
    document.querySelectorAll("[data-quantity-plus]").forEach((button) => {
      button.addEventListener("click", () => this.changeQuantity(button, 1));
    });
    document.querySelectorAll("[data-product-tab]").forEach((button) => {
      button.addEventListener("click", () => this.changeProductTab(button));
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.closeMenu();
        this.closeSizeGuide();
        this.closeWishlist();
        this.closeCart();
      }
    });
  }

  onScroll() {
    const currentY = window.scrollY;
    const delta = currentY - this.lastScrollY;
    if (this.header) {
      this.header.classList.toggle("is-scrolled", currentY > 42);
      if (currentY < 90 || delta < -7) {
        this.header.classList.remove("is-hidden");
      } else if (
        delta > 7 &&
        currentY > 130 &&
        this.header.dataset.hideOnScroll !== "false" &&
        !document.body.classList.contains("t-overlay-open")
      ) {
        this.header.classList.add("is-hidden");
      }
    }
    this.updateMobileDock(currentY, delta);
    this.lastScrollY = currentY;
  }

  bindMobileDock() {
    this.mobileDock = document.querySelector("[data-mobile-dock]");
    if (!this.mobileDock || this.mobileDock.dataset.dockBound === "true") return;
    this.mobileDock.dataset.dockBound = "true";

    /* The dock had two sizes and swapped between them on scroll, so it grew and
       shrank under the thumb as the page moved — motion with no meaning, and
       the main reason it read as unfinished. It is one size now. These remain
       as no-ops so the existing scroll bindings keep their shape. */
    const expand = () => {};
    const settle = () => {};

    this.mobileDock.addEventListener("pointerenter", expand);
    this.mobileDock.addEventListener("pointerleave", settle);
    this.mobileDock.addEventListener("focusin", expand);
    this.mobileDock.addEventListener("focusout", settle);
    this.mobileDock.addEventListener("pointerdown", (event) => {
      expand();
      const item = event.target.closest("a, button");
      if (!item) return;
      item.classList.add("is-pressed");
      window.setTimeout(() => item.classList.remove("is-pressed"), 180);
    });
    this.mobileDock.addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      const items = Array.from(this.mobileDock.querySelectorAll(":scope > a, :scope > button"));
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(event.clientX - (rect.left + rect.width / 2));
        const influence = Math.exp(-(distance * distance) / (2 * 74 * 74));
        item.style.setProperty("--dock-lift", influence.toFixed(3));
      });
    });
    this.mobileDock.addEventListener("pointerleave", () => {
      this.mobileDock.querySelectorAll(":scope > a, :scope > button").forEach((item) => item.style.removeProperty("--dock-lift"));
    });
  }

  updateMobileDock() {
    /* Intentionally inert: the dock no longer resizes on scroll. */
  }

  bindStoryVideos() {
    document.querySelectorAll("[data-video-stories]").forEach((section) => {
      if (section.dataset.videoBound === "true") return;
      section.dataset.videoBound = "true";

      const rail = section.querySelector("[data-video-rail]");
      const videos = section.querySelectorAll(".t-video-story__video");
      if (!this.designMode && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.play().catch(() => {});
              } else {
                entry.target.pause();
              }
            });
          },
          { threshold: 0.62 },
        );
        videos.forEach((video) => observer.observe(video));
      } else {
        videos.forEach((video) => video.pause());
      }

      const scrollRail = (direction) => {
        if (!rail) return;
        rail.scrollBy({
          left: direction * Math.max(280, rail.clientWidth * 0.72),
          behavior: "smooth",
        });
      };
      const edgeBack = section.querySelector("[data-video-edge-prev]");
      const edgeNext = section.querySelector("[data-video-edge-next]");
      const updateEdges = () => {
        if (!rail) return;
        const back = rail.scrollLeft > 8;
        const next = rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8;
        edgeBack?.classList.toggle("is-visible", back);
        edgeNext?.classList.toggle("is-visible", next);
        edgeBack?.setAttribute("aria-hidden", String(!back));
        edgeNext?.setAttribute("aria-hidden", String(!next));
      };
      section.querySelector("[data-video-rail-prev]")?.addEventListener("click", () => scrollRail(-1));
      section.querySelector("[data-video-rail-next]")?.addEventListener("click", () => scrollRail(1));
      edgeBack?.addEventListener("click", () => scrollRail(-1));
      edgeNext?.addEventListener("click", () => scrollRail(1));
      rail?.addEventListener("scroll", updateEdges, { passive: true });
      window.addEventListener("resize", updateEdges, { passive: true });
      updateEdges();
      rail?.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") scrollRail(-1);
        if (event.key === "ArrowRight") scrollRail(1);
      });

      section.querySelectorAll("[data-video-toggle]").forEach((button) => {
        const video = button.closest(".t-video-story")?.querySelector("video");
        if (!video) return;
        const sync = () => {
          const paused = video.paused;
          const playIcon = button.querySelector("[data-video-play]");
          const pauseIcon = button.querySelector("[data-video-pause]");
          if (playIcon) playIcon.hidden = !paused;
          if (pauseIcon) pauseIcon.hidden = paused;
          button.setAttribute("aria-label", `${paused ? "Play" : "Pause"} video`);
        };
        button.addEventListener("click", () => {
          if (video.paused) video.play().catch(() => {});
          else video.pause();
        });
        video.addEventListener("play", sync);
        video.addEventListener("pause", sync);
        sync();
      });
    });
  }

  bindHeroVideos() {
    document.querySelectorAll("[data-hero-speed]").forEach((hero) => {
      if (hero.dataset.heroBound === "true") return;
      hero.dataset.heroBound = "true";
      const speed = Math.min(2, Math.max(0.5, Number(hero.dataset.heroSpeed || 1)));
      hero.querySelectorAll("video").forEach((video) => {
        if (this.designMode) video.pause();
        const applySpeed = () => {
          video.defaultPlaybackRate = speed;
          video.playbackRate = speed;
        };
        applySpeed();
        video.addEventListener("loadedmetadata", applySpeed);
      });
    });
  }

  bindHeroCarousels() {
    document.querySelectorAll("[data-hero-carousel]").forEach((carousel) => {
      if (carousel.dataset.carouselBound === "true") return;
      carousel.dataset.carouselBound = "true";

      const slides = Array.from(carousel.querySelectorAll("[data-hero-slide]"));
      const dots = Array.from(carousel.querySelectorAll("[data-hero-dot]"));
      const counter = carousel.querySelector("[data-hero-counter]");
      const duration = Math.max(3000, Number(carousel.dataset.heroDuration || 7000));
      const autoplay =
        !this.designMode &&
        carousel.dataset.heroAutoplay === "true" &&
        slides.length > 1;
      let active = 0;
      let timer;
      let touchStart = 0;

      carousel.style.setProperty("--hero-slide-duration", `${duration}ms`);

      const show = (index) => {
        active = (index + slides.length) % slides.length;
        carousel.dataset.activeTone = slides[active]?.dataset.headerTone || "light";
        carousel.dataset.activeMobileTone =
          slides[active]?.dataset.mobileHeaderTone ||
          carousel.dataset.activeTone;
        if (this.header) {
          this.header.dataset.heroTone = carousel.dataset.activeTone;
          this.header.dataset.mobileHeroTone = carousel.dataset.activeMobileTone;
        }
        slides.forEach((slide, slideIndex) => {
          const selected = slideIndex === active;
          slide.classList.toggle("is-active", selected);
          slide.setAttribute("aria-hidden", String(!selected));
          slide.querySelectorAll("video").forEach((video) => {
            if (selected && !this.designMode) video.play().catch(() => {});
            else video.pause();
          });
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle("is-active", dotIndex === active);
          if (dotIndex === active) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
        if (counter) counter.textContent = `${String(active + 1).padStart(2, "0")} / ${String(slides.length).padStart(2, "0")}`;
      };

      const stop = () => {
        window.clearInterval(timer);
        carousel.classList.add("is-paused");
      };
      const start = () => {
        window.clearInterval(timer);
        carousel.classList.remove("is-paused");
        if (autoplay && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          timer = window.setInterval(() => show(active + 1), duration);
        }
      };
      const move = (direction) => {
        show(active + direction);
        start();
      };

      carousel.querySelector("[data-hero-prev]")?.addEventListener("click", () => move(-1));
      carousel.querySelector("[data-hero-next]")?.addEventListener("click", () => move(1));
      dots.forEach((dot, index) => dot.addEventListener("click", () => {
        show(index);
        start();
      }));
      if (carousel.dataset.heroPause === "true") {
        /* Pause on hover and a full-height hero are a contradiction: the hero
           is the whole viewport, so on a desktop the pointer is over it from
           the moment the page loads. mouseenter fired, the timer was cleared,
           the progress bar froze full, and the slide never advanced — which is
           exactly what it looked like. On a full-bleed hero, only the
           navigation cluster pauses it; a shorter hero keeps the old
           behaviour, where hovering it is a deliberate act. */
        const fullBleed = carousel.classList.contains("t-hero-carousel--full");
        const pauseZone = fullBleed
          ? carousel.querySelector(".t-hero-carousel__navigation") || carousel
          : carousel;
        pauseZone.addEventListener("mouseenter", stop);
        pauseZone.addEventListener("mouseleave", start);
        carousel.addEventListener("focusin", stop);
        carousel.addEventListener("focusout", start);
      }
      carousel.addEventListener("touchstart", (event) => {
        touchStart = event.changedTouches[0]?.clientX || 0;
      }, { passive: true });
      carousel.addEventListener("touchend", (event) => {
        const distance = (event.changedTouches[0]?.clientX || 0) - touchStart;
        if (Math.abs(distance) > 48) move(distance > 0 ? -1 : 1);
      }, { passive: true });

      /* With the arrows switched off there was no way to change slide on a
         desktop except the dots, which are 30px wide. A trackpad's horizontal
         swipe and the arrow keys both move it now. */
      /* Press and drag, which is the gesture a mouse actually has. The wheel
         handler below only ever fired on a trackpad's horizontal axis, so on a
         mouse the arrows keys were the only way to move — which is what it
         looked like. */
      let dragX = null;
      let dragged = 0;
      carousel.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "touch" || event.button !== 0) return;
        if (event.target.closest("a, button")) return;
        dragX = event.clientX;
        dragged = 0;
        carousel.classList.add("is-dragging");
      });
      carousel.addEventListener("pointermove", (event) => {
        if (dragX === null) return;
        dragged = event.clientX - dragX;
      });
      const endDrag = () => {
        if (dragX === null) return;
        const travelled = dragged;
        dragX = null;
        dragged = 0;
        carousel.classList.remove("is-dragging");
        if (Math.abs(travelled) > 60) move(travelled > 0 ? -1 : 1);
      };
      carousel.addEventListener("pointerup", endDrag);
      carousel.addEventListener("pointerleave", endDrag);
      carousel.addEventListener("pointercancel", endDrag);
      /* A drag that travelled must not also follow the slide's link. */
      carousel.addEventListener("click", (event) => {
        if (Math.abs(dragged) > 6) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      let wheelLock = 0;
      carousel.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaX) < 24 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        const now = Date.now();
        if (now - wheelLock < 600) return;
        wheelLock = now;
        move(event.deltaX > 0 ? 1 : -1);
      }, { passive: true });

      carousel.setAttribute("tabindex", carousel.getAttribute("tabindex") || "0");
      carousel.addEventListener("keydown", (event) => {
        if (event.key === "ArrowRight") { event.preventDefault(); move(1); }
        else if (event.key === "ArrowLeft") { event.preventDefault(); move(-1); }
      });

      show(0);
      start();
    });
  }

  /* A horizontal rail is obvious on a phone, where you swipe it, and invisible
     on a desktop, where a mouse has no horizontal axis. These rails now take a
     press-and-drag and a trackpad's sideways scroll. */
  /* Apply appears once there is something to apply. Bonkers does this and it is
     right: an always-on button next to an empty field is a control that does
     nothing, and it competes with Checkout for attention. */
  /* Photography is the product on a fashion site, and it currently snaps in as
     each file lands. A fade on decode costs two properties and is the last
     obviously-cheap moment left on a grid that is otherwise composed. */
  /* The drawer's markup arrives long after GoKwik has scanned the page, so its
     Buy now button is still sitting disabled. This wakes it.

     Additive only: it enables GoKwik's button when the gateway is live, and
     reveals Shopify's fallback when the gateway has already been given up on.
     It never hides a GoKwik button — an earlier version of this idea did, and
     because GoKwik injects its own DOM while loading, its own arrival kept
     re-triggering the code that hid it. Hiding belongs to the one-shot fallback
     in snippets/gokwik.liquid and nowhere else. */
  releaseGokwikIn(root) {
    if (!root) return;

    /* `.gokwik-checkout button` matters as much as the buy-now buttons.

       gokwik-checkout.liquid ships its button with the `disabled` attribute and
       relies on the gateway to enable it. This method was the only thing in the
       theme that removes it, it only ever looked for `[data-gokwik-buy-now]`,
       and it was never called on the cart drawer at all — so the checkout
       button sat there disabled. Not covered by anything, not unreachable:
       disabled. That is the whole of "the checkout button does not work". */
    const live =
      typeof window.onCheckoutClick === "function" ||
      (typeof window.onBuyNowClick === "function" && !window.onBuyNowClick.__tactStub);

    if (live) {
      root
        .querySelectorAll("[data-gokwik-buy-now], .gokwik-checkout button[disabled], .gokwik-checkout button.disabled")
        .forEach((button) => {
          button.disabled = false;
          button.classList.remove("disabled");
        });
      return;
    }

    if (document.documentElement.classList.contains("t-gokwik-unavailable")) {
      this.revealCheckoutFallbackIn(root);
    }
  }

  /* Shopify's own checkout, shown when the gateway did not arrive. A real form
     submit, so it needs nothing of ours to work. */
  revealCheckoutFallbackIn(root) {
    if (!root) return;
    root.querySelectorAll("[data-gokwik-fallback]").forEach((el) => {
      el.hidden = false;
      el.removeAttribute("style");
    });
    /* The gateway's own button is dead in this state; leaving it above the
       working one just gives the shopper something to click that does nothing. */
    root.querySelectorAll(".gokwik-checkout").forEach((el) => {
      if (el.querySelector("button[disabled], button.disabled")) el.hidden = true;
    });
  }

  /* There must always be a checkout button that works.

     Called whenever the drawer opens or re-renders, again when the gateway
     announces itself, and once more on a hard timer — because the failure this
     guards against is the gateway never announcing itself at all, and in that
     case nothing else would ever run. */
  ensureCheckoutClickable() {
    const roots = [this.cartDrawer, document.querySelector("[data-cart-page]")].filter(Boolean);
    roots.forEach((root) => this.releaseGokwikIn(root));

    if (this.checkoutWatchdog) return;
    this.checkoutWatchdog = window.setTimeout(() => {
      const live =
        typeof window.onCheckoutClick === "function" ||
        (typeof window.onBuyNowClick === "function" && !window.onBuyNowClick.__tactStub);
      roots.forEach((root) => {
        if (live) this.releaseGokwikIn(root);
        else this.revealCheckoutFallbackIn(root);
      });
    }, 2600);
  }

  bindImageReveal() {
    if (this.reduceMotion) return;
    document.documentElement.classList.add("t-img-reveal");

    const show = (img) => img.classList.add("is-shown");
    const watch = (img) => {
      if (img.dataset.revealBound === "true") return;
      img.dataset.revealBound = "true";
      /* `complete` means the browser has finished, successfully or not. Testing
         naturalWidth as well would skip images that already failed — their
         error event fired before this listener existed, so nothing would ever
         show them and they would stay invisible. Done is done. */
      if (img.complete) return show(img);
      img.addEventListener("load", () => show(img), { once: true });
      img.addEventListener("error", () => show(img), { once: true });
    };

    const scan = () =>
      document
        .querySelectorAll(".t-product-card__media > img:first-of-type, .t-collection-tile img")
        .forEach(watch);

    scan();
    /* Grids paginate and filter in place, so cards arrive after boot. */
    document.addEventListener("tact:products-rendered", scan);
    window.setTimeout(scan, 1200);
  }

  bindFooterMenus() {
    const menus = [...document.querySelectorAll("[data-footer-menu]")];
    if (!menus.length) return;
    const wide = window.matchMedia("(min-width: 750px)");
    const layout = () => menus.forEach((menu) => {
      menu.open = wide.matches;
    });
    wide.addEventListener?.("change", layout);
    layout();
  }

  bindCouponField() {
    document.addEventListener("input", (event) => {
      const input = event.target.closest(".t-cart-coupon__row input");
      if (!input) return;
      input.closest(".t-cart-coupon__row")?.classList.toggle("has-code", input.value.trim().length > 0);
    });

    /* This lived inside bindCartDrawer, which returns early when there is no
       drawer on the page. The same promo form is rendered on the cart page, so
       on any page without a drawer the handler was never registered at all and
       the form fell through to a native submit — a full page load, straight
       out of the theme. It belongs with the field it serves. */
    if (this.couponBound) return;
    this.couponBound = true;

    /* Applying a promo code must not leave the page.

       This sent the shopper to `/discount/CODE?redirect=/checkout`, which did
       two things wrong at once: it navigated away from the cart, and it landed
       them in *Shopify's* checkout — walking straight past GoKwik, which is the
       gateway this store actually takes payment through. On a live store that
       is a lost order, not a cosmetic bug.

       Shopify's discount endpoint sets the cart's discount cookie server-side.
       Fetching it in the background does exactly the same thing without moving
       anyone: the code is on the cart, and checkout — GoKwik's or Shopify's —
       picks it up from there. */
    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("[data-cart-discount]");
      if (!form) return;
      event.preventDefault();
      const code = String(new FormData(form).get("discount") || "").trim();
      const message = form.querySelector("[data-cart-discount-message]");
      const say = (text, state) => {
        if (!message) return;
        message.textContent = text;
        message.dataset.state = state || "";
      };
      if (!code) return say("Enter a promo code.", "error");

      form.classList.add("is-applying");
      say("Applying…", "busy");
      try {
        const response = await fetch(this.shopifyPath(`discount/${encodeURIComponent(code)}`), {
          method: "GET",
          credentials: "same-origin",
          redirect: "follow",
          headers: { Accept: "text/html" },
        });
        /* A code the shop will not take is not a broken page. Say so and stay
           put — reloading to prove a typo wrong is the worst of both. */
        if (!response.ok) {
          say("That code isn’t valid for this bag.", "error");
          return;
        }
        /* Re-read the cart so the drawer shows whatever the code changed. */
        const cart = await this.fetchCart();
        this.updateCartIndicators(cart);
        this.syncCartDrawer(cart);
        say(`${code.toUpperCase()} applied. It shows at checkout.`, "ok");
        form.classList.add("is-applied");
        form.querySelector("input[name='discount']")?.blur();
      } catch {
        /* Only a request that could not be made at all falls back to the
           endpoint directly — and it returns to the bag, never to Shopify's
           checkout, because that is the gateway this store does not use. */
        say("Applying…", "busy");
        window.location.assign(
          `${this.shopifyPath(`discount/${encodeURIComponent(code)}`)}?redirect=${encodeURIComponent(this.shopifyPath("cart"))}`
        );
      } finally {
        form.classList.remove("is-applying");
      }
    });
  }

  bindCartOffers() {
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-cart-offers-toggle]");
      if (toggle) {
        const root = toggle.closest("[data-cart-offers]");
        const panel = root?.querySelector("[data-cart-offers-panel]");
        if (!panel) return;
        const open = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", open ? "false" : "true");
        root.classList.toggle("is-open", !open);
        panel.hidden = open;
        if (!open) this.buildCartOffers(panel);
        return;
      }

      /* Applying an offer is one tap. */
      const claim = event.target.closest("[data-offer-apply]");
      if (!claim) return;
      const code = claim.dataset.offerApply;
      const form = document.querySelector("[data-cart-discount]");
      const input = form?.querySelector("input[name='discount']");
      if (!form || !input || !code) return;
      input.value = code;
      input.closest(".t-cart-coupon__row")?.classList.add("has-code");
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    });
  }

  /* Turn the merchant's rich text into offers you can actually take.

     The offers panel is one richtext setting — a paragraph per offer, the code
     in bold at the front. Read as prose that is fine; used as a cart control it
     meant reading a code, selecting it, copying it and typing it into a field
     two inches above, which is four steps to claim a discount the shop is
     trying to give away.

     Each paragraph becomes a row: the code as a chip, the terms beside it, and
     an Apply that fills the field and submits it. Offers with no code to type —
     prepaid, free delivery — say so instead of offering a button that would do
     nothing. The merchant keeps writing plain paragraphs; nothing about the
     setting changes. */
  buildCartOffers(panel) {
    if (!panel || panel.dataset.offersBuilt === "true") return;

    /* The panel is server-rendered now: the tiers produce a real available /
       locked list in Liquid, with the codes, the shortfall and the buttons
       already in place. This function exists for the merchant's free-text
       block, which is still just paragraphs. Rebuilding a panel that already
       has structure would tear that structure out and re-emit it as a flat
       list of whatever <p> tags it could find — including the two headings. */
    if (panel.querySelector(".t-offers")) {
      panel.dataset.offersBuilt = "true";
      const written = panel.querySelector(".t-cart-offers__written");
      if (written) this.buildOfferRows(written);
      return;
    }

    this.buildOfferRows(panel);
  }

  buildOfferRows(panel) {
    const rows = Array.from(panel.querySelectorAll("p"));
    if (!rows.length) return;
    panel.dataset.offersBuilt = "true";

    const list = document.createElement("ul");
    list.className = "t-offers";

    rows.forEach((row) => {
      const lead = row.querySelector("strong, b");
      const label = (lead?.textContent || "").trim();
      const rest = row.textContent.replace(label, "").replace(/^[\s—–-]+/, "").trim();

      /* A code is one word and carries a digit or is shouted in capitals.
         "Prepaid" and "Free delivery" are conditions, not codes. */
      const isCode = !!label && !/\s/.test(label) && (/\d/.test(label) || label === label.toUpperCase());

      const item = document.createElement("li");
      item.className = `t-offers__row${isCode ? "" : " is-automatic"}`;
      item.innerHTML = `
        <div class="t-offers__copy">
          ${label ? `<span class="t-offers__code">${this.escapeHtml(label)}</span>` : ""}
          ${rest ? `<span class="t-offers__terms">${this.escapeHtml(rest)}</span>` : ""}
        </div>
        ${
          isCode
            ? `<button class="t-offers__apply" type="button" data-offer-apply="${this.escapeHtml(label)}">Apply</button>`
            : `<span class="t-offers__auto">Automatic</span>`
        }`;
      list.appendChild(item);
    });

    panel.replaceChildren(list);
  }

  bindDragRails() {
    const rails = document.querySelectorAll(
      "[data-cart-upsell-rail], .t-cart-drawer__upsell-rail, .t-complementary__rail, [data-drag-rail]",
    );
    rails.forEach((rail) => {
      if (rail.dataset.dragBound === "true") return;
      rail.dataset.dragBound = "true";

      let down = false;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      rail.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "touch") return;
        down = true;
        moved = 0;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add("is-dragging");
      });

      rail.addEventListener("pointermove", (event) => {
        if (!down) return;
        const delta = event.clientX - startX;
        moved = Math.max(moved, Math.abs(delta));
        rail.scrollLeft = startScroll - delta;
        if (moved > 4) event.preventDefault();
      });

      const release = () => {
        if (!down) return;
        down = false;
        rail.classList.remove("is-dragging");
      };
      rail.addEventListener("pointerup", release);
      rail.addEventListener("pointerleave", release);
      rail.addEventListener("pointercancel", release);

      /* A drag that travelled should not also open the card it ended on. */
      rail.addEventListener("click", (event) => {
        if (moved > 4) {
          event.preventDefault();
          event.stopPropagation();
          moved = 0;
        }
      }, true);

      /* A vertical wheel over a horizontal rail scrolls the rail, not the page
         behind it — but only while the rail still has somewhere to go. */
      rail.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        const max = rail.scrollWidth - rail.clientWidth;
        if (max <= 1) return;
        const next = rail.scrollLeft + event.deltaY;
        if (next < 0 || next > max) return;
        event.preventDefault();
        rail.scrollLeft = next;
      }, { passive: false });
    });
  }

  bindReviewStories() {
    document.querySelectorAll("[data-review-stories]").forEach((section) => {
      if (section.dataset.reviewBound === "true") return;
      section.dataset.reviewBound = "true";
      const slides = Array.from(section.querySelectorAll("[data-review-slide]"));
      const dots = Array.from(section.querySelectorAll("[data-review-dot]"));
      if (!slides.length) return;
      let active = 0;
      let timer;

      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle("is-active", slideIndex === active);
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle("is-active", dotIndex === active);
          if (dotIndex === active) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (this.designMode) return;
        const interval = Math.max(3000, Number(section.dataset.reviewInterval || 5000));
        timer = window.setInterval(() => show(active + 1), interval);
      };

      section.querySelector("[data-review-prev]")?.addEventListener("click", () => {
        show(active - 1);
        start();
      });
      section.querySelector("[data-review-next]")?.addEventListener("click", () => {
        show(active + 1);
        start();
      });
      dots.forEach((dot, index) => {
        dot.addEventListener("click", () => {
          show(index);
          start();
        });
      });
      section.addEventListener("mouseenter", stop);
      section.addEventListener("mouseleave", start);
      section.addEventListener("focusin", stop);
      section.addEventListener("focusout", start);
      show(0);
      start();
    });
  }

  bindVideoQuickAdd() {
    /* Delegated, not bound per form.

       syncCartDrawer() replaces the drawer's entire innerHTML after every cart
       change, so any listener attached to a form inside it dies with the swap —
       and the fresh markup arrives without the `quickAddBound` marker, so a
       re-run could not tell it had already been handled. An unbound
       `<form action="/cart/add">` does what the browser does with it: a real
       POST, which Shopify answers with a redirect to /cart. That is why adding
       from "Pairs well with" threw the shopper out of the drawer and onto the
       cart page, but only after they had already changed something.

       A listener on the document is immune to the swap: the form does not own
       it. Submit bubbles, so this catches every quick-add form on the page —
       the film, the cart upsell and the paired cards alike — including ones
       that did not exist when the script ran. */
    if (this.quickAddBound) return;
    this.quickAddBound = true;
    document.addEventListener("submit", async (event) => {
        const form = event.target?.closest?.("[data-quick-add]");
        if (!form) return;
        event.preventDefault();
        const button = form.querySelector("button");
        if (!button || button.disabled) return;
        const label = button.querySelector("[data-quick-add-label]");
        const status = form.querySelector("[data-quick-add-status]");
        const defaultLabel = label?.textContent || "";
        let resetDelay = 1300;
        const setState = (state, message) => {
          form.classList.remove("is-loading", "is-added", "is-error");
          button.classList.remove("is-loading", "is-added", "is-error");
          form.classList.add(state);
          button.classList.add(state);
          if (label && message) label.textContent = message;
          if (status) status.textContent = message || "";
        };

        button.disabled = true;
        setState("is-loading", "Adding…");
        button.setAttribute("aria-busy", "true");

        try {
          const added = await this.requestJson("cart/add.js", {
            method: "POST",
            body: new FormData(form),
          });
          this.pixelAddToCart(added);

          setState("is-added", "Added");
          button.removeAttribute("aria-busy");
          const cart = await this.fetchCart();
          this.updateCartIndicators(cart);
          await this.animateAddToCart(form);
          document.dispatchEvent(new CustomEvent("tact:cart-updated", { detail: { cart, open: true } }));
          const activeSheetId = form.closest("[data-card-quick-sheet]")?.id || "";
          let productCard = form.closest("[data-product-card]");
          if (!productCard) {
            productCard = activeSheetId
              ? Array.from(document.querySelectorAll("[data-product-card]")).find((card) => card.querySelector(`[aria-controls="${CSS.escape(activeSheetId)}"]`))
              : null;
          }
          if (productCard) {
            window.setTimeout(() => {
              productCard.classList.remove("is-quick-open");
              productCard.querySelector("[data-card-quick-open]")?.setAttribute("aria-expanded", "false");
              const sheet = activeSheetId ? document.getElementById(activeSheetId) : null;
              sheet?.classList.remove("is-open");
              sheet?.setAttribute("aria-hidden", "true");
              if (!document.querySelector("[data-product-card].is-quick-open")) {
                document.body.classList.remove("t-quick-sheet-open");
              }
            }, 720);
          }
        } catch (error) {
          resetDelay = 2400;
          setState("is-error", this.friendlyCartError(error));
        } finally {
          window.setTimeout(() => {
            button.disabled = false;
            form.classList.remove("is-loading", "is-added", "is-error");
            button.classList.remove("is-loading", "is-added", "is-error");
            button.removeAttribute("aria-busy");
            if (label) label.textContent = defaultLabel;
            if (status) status.textContent = "";
          }, resetDelay);
        }
    });
  }

  bindProductForms() {
    document.querySelectorAll(".t-product-form").forEach((form) => {
      if (form.dataset.productFormBound === "true") return;
      form.dataset.productFormBound = "true";

      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[name="add"]');
        const label = button?.querySelector("span");
        const message = form.querySelector("[data-product-message]");
        if (!button || button.disabled) return;

        const initialLabel = label?.textContent || "Add to bag";
        button.disabled = true;
        button.classList.add("is-loading");
        button.setAttribute("aria-busy", "true");
        if (label) label.textContent = "Adding…";
        if (message) message.textContent = "";

        try {
          const added = await this.requestJson("cart/add.js", {
            method: "POST",
            body: new FormData(form),
          });
          this.pixelAddToCart(added);

          if (label) label.textContent = "Added";
          button.classList.remove("is-loading");
          button.classList.add("is-added");
          if (message) message.textContent = "Added to your bag.";
          const cart = await this.fetchCart();
          this.updateCartIndicators(cart);
          await this.animateAddToCart(form);
          document.dispatchEvent(new CustomEvent("tact:cart-updated", { detail: { cart, open: true } }));
        } catch (error) {
          button.classList.remove("is-loading");
          button.classList.add("is-error");
          if (label) label.textContent = this.friendlyCartError(error);
          if (message) message.textContent = error?.message || "This item could not be added.";
        } finally {
          button.removeAttribute("aria-busy");
          window.setTimeout(() => {
            button.disabled = false;
            button.classList.remove("is-loading", "is-added", "is-error");
            if (label) label.textContent = initialLabel;
          }, 1800);
        }
      });
    });
  }

  bindCategoryDecks() {
    document.querySelectorAll("[data-category-deck]").forEach((deck) => {
      if (deck.dataset.categoryBound === "true") return;
      deck.dataset.categoryBound = "true";

      const rail = deck.querySelector("[data-category-rail]");
      const progress = deck.querySelector("[data-category-progress]");
      if (!rail) return;

      const update = () => {
        const distance = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const amount = distance ? Math.min(1, rail.scrollLeft / distance) : 1;
        if (progress) progress.style.transform = `scaleX(${Math.max(.12, amount)})`;
        deck.querySelector("[data-category-prev]")?.toggleAttribute("disabled", rail.scrollLeft < 8);
        deck
          .querySelector("[data-category-next]")
          ?.toggleAttribute("disabled", rail.scrollLeft >= distance - 8);
      };
      const move = (direction) => {
        const card = rail.querySelector(".t-collection-tile");
        const distance = card ? card.getBoundingClientRect().width + 12 : rail.clientWidth * .75;
        rail.scrollBy({ left: distance * direction, behavior: "smooth" });
      };

      deck.querySelector("[data-category-prev]")?.addEventListener("click", () => move(-1));
      deck.querySelector("[data-category-next]")?.addEventListener("click", () => move(1));
      rail.addEventListener("scroll", update, { passive: true });
      rail.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") move(-1);
        if (event.key === "ArrowRight") move(1);
      });
      window.addEventListener("resize", update, { passive: true });
      update();
    });
  }

  bindCollectionFilters() {
    document.querySelectorAll("[data-collection-filters]").forEach((form) => {
      if (form.dataset.filtersBound === "true") return;
      form.dataset.filtersBound = "true";
      const toggle = form.querySelector("[data-filter-toggle]");
      const panel = form.querySelector("[data-filter-panel]");
      if (!toggle || !panel) return;
      toggle.addEventListener("click", () => {
        const open = form.classList.toggle("is-filter-open");
        toggle.setAttribute("aria-expanded", String(open));
      });
    });
  }

  async animateAddToCart(source) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const card = source.closest(".t-product-card, .t-video-story, .t-product");
    const image = card?.querySelector("img");
    const target = [...document.querySelectorAll("[data-cart-open]")].find((node) => node.offsetParent !== null);
    if (!image || !target) return;

    const from = image.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const flight = image.cloneNode();
    flight.className = "t-cart-flight";
    flight.removeAttribute("srcset");
    Object.assign(flight.style, {
      left: `${from.left}px`, top: `${from.top}px`, width: `${Math.min(from.width, 112)}px`,
      height: `${Math.min(from.height, 112)}px`,
    });
    document.body.append(flight);
    const animation = flight.animate([
      { transform: "translate3d(0,0,0) scale(1)", opacity: 1 },
      { transform: `translate3d(${to.left - from.left}px, ${to.top - from.top}px, 0) scale(.16)`, opacity: .2 },
    ], { duration: 520, easing: "cubic-bezier(.22,.8,.28,1)", fill: "forwards" });
    await animation.finished.catch(() => {});
    flight.remove();
  }

  bindCartDrawer() {
    this.cartDrawer = document.querySelector("[data-cart-drawer]");
    this.cartOverlay = document.querySelector(".t-cart-drawer-overlay");
    if (!this.cartDrawer || document.body.dataset.cartDrawerBound === "true") return;
    document.body.dataset.cartDrawerBound = "true";

    document.addEventListener("click", (event) => {
      const opener = event.target.closest("[data-cart-open]");
      if (opener) {
        /* Do not preventDefault when Kwik Cart owns the bag — swallowing the
           click would stop their cart opening and leave the shopper with
           nothing. */
        if (this.gokwikCartPresent()) return;
        event.preventDefault();
        this.openCart(true);
        return;
      }
      if (event.target.closest("[data-cart-close]")) {
        this.closeCart();
        return;
      }
      const quantity = event.target.closest("[data-cart-quantity]");
      if (quantity) {
        const line = quantity.closest("[data-cart-line]");
        /* The same stepper serves two surfaces: in the drawer the number is a
           <b>, on the cart page it is the form field itself, because that field
           is what makes the page work with scripting off. Read whichever is
           there. */
        const readout = line?.querySelector("[data-cart-readout]")
          || line?.querySelector(".t-cart-drawer__quantity b");
        const current = Number((readout && ("value" in readout ? readout.value : readout.textContent)) || 1);
        const next = Math.max(0, current + Number(quantity.dataset.cartQuantity));

        /* The number moves now, not when the network says so. Waiting on a
           round trip to redraw a digit the shopper already chose is what made
           this feel slow — and tapping + three times fired three requests, each
           one redrawing the drawer underneath the finger. The line shows it is
           settling, the requests coalesce, and the server is the last word. */
        if (readout) {
          if ("value" in readout) readout.value = String(next);
          else readout.textContent = String(next);
        }
        line?.classList.add("is-pending");
        line?.classList.toggle("is-removing", next === 0);
        this.queueCartLine(quantity.dataset.cartKey, next);
        return;
      }
      const remove = event.target.closest("[data-cart-remove]");
      if (remove) {
        /* On the cart page this is an anchor pointing at Shopify's own remove
           URL — the no-script path. With script, take the click. */
        event.preventDefault();
        remove.closest("[data-cart-line]")?.classList.add("is-pending", "is-removing");
        this.changeCartLine(remove.dataset.cartKey, 0);
      }
    });

    /* The gateway loading late is the normal case, not the exception. */
    document.addEventListener("gokwikLoaded", () => this.ensureCheckoutClickable());
    window.addEventListener("gokwikLoaded", () => this.ensureCheckoutClickable());

    document.addEventListener("tact:cart-updated", (event) => {
      const cart = event.detail?.cart || event.detail;
      if (!cart) return;
      this.syncCartDrawer(cart);
      /* Only open a drawer that is closed. Adding from the upsell inside an
         already-open drawer re-ran openCart, which replays the slide-in and
         the line stagger — so the bag appeared to close and come back rather
         than simply gaining a line. An open drawer just syncs. */
      if (event.detail?.open === true && !this.cartDrawer?.classList.contains("is-open")) {
        this.openCart(false);
      }
    });

  }

  async fetchCart() {
    return this.requestJson("cart.js");
  }

  async hydrateCart() {
    try {
      const cart = await this.fetchCart();
      this.updateCartIndicators(cart);
      this.syncCartDrawer(cart);
    } catch {
      // Keep native cart links usable when the Ajax endpoint is temporarily unavailable.
    }
  }

  friendlyCartError(error) {
    const message = String(error?.message || "").toLowerCase();
    if (error?.name === "AbortError") return "Connection slow";
    if (error?.status === 422 || /sold out|unavailable|not available|inventory/.test(message)) {
      return "Unavailable";
    }
    return "Couldn’t add";
  }

  /* GoKwik's Kwik Cart is a separate app with its own slide-out cart — the one
     the reference storefront actually runs. When it is installed it binds the
     bag itself, so two carts end up fighting over the same click: ours opens,
     theirs opens behind it, and the page locks scroll twice. If it is present,
     this theme's drawer stands down completely and lets the app own the cart.

     Detected from its script and its own DOM rather than a setting, because
     the merchant installs it in Shopify, not here. */
  gokwikCartPresent() {
    if (this.gokwikCartChecked !== undefined) return this.gokwikCartChecked;
    const byGlobal = Boolean(window.gokwikCart || window.KwikCart || window.kwikCart);
    const byNode = Boolean(
      document.querySelector("#gk-cart, .kwik-cart, [data-gokwik-cart], [id^='gokwik-cart'], [class*='kwik-cart']"),
    );
    const byScript = [...document.scripts].some((script) =>
      /kwikcart|side-cart\.js/i.test(script.src || ""),
    );
    this.gokwikCartChecked = byGlobal || byNode || byScript;
    return this.gokwikCartChecked;
  }

  updateCartIndicators(cart) {
    const countValue = Math.max(0, Number(cart?.item_count) || 0);
    document.querySelectorAll("[data-cart-open]").forEach((bag) => {
      /* Every count in the bag, not the first one.

         The bag carries two: `.t-header__word-count` for the worded header and
         `.t-header__count` for the badge on the icon, and which of them is
         visible depends on the breakpoint. `querySelector` updated only the
         first, so on a phone — where the badge is the visible one — the number
         was whatever the page had been rendered with and never moved again.
         Remove a piece and the bag still claimed ten. The wishlist counter next
         to it has always used querySelectorAll; this is the same job. */
      let counts = bag.querySelectorAll("[data-cart-count]");
      if (!counts.length) {
        const count = document.createElement("span");
        count.className = "t-header__count";
        count.dataset.cartCount = "";
        bag.appendChild(count);
        counts = [count];
      }
      counts.forEach((count) => {
        count.textContent = String(countValue);
        count.hidden = countValue === 0;
      });
      bag.setAttribute("aria-label", `Bag with ${countValue} ${countValue === 1 ? "item" : "items"}`);
    });
  }

  /* The cart page is a fallback, not a destination.

     The drawer is the cart, so /cart should never be somewhere a shopper ends
     up: it duplicates the drawer, drops them out of their browsing context, and
     is a second place every cart feature has to be maintained. It is not
     deleted though — with JavaScript off the drawer cannot run, and the page is
     then the only way to see a bag or reach checkout, so Shopify's own route
     stays intact and keeps working. This just makes it unreachable whenever the
     drawer is available: leave for wherever the shopper came from, and open the
     drawer when we land. */
  bindCartPageRedirect() {
    const root = window.Shopify?.routes?.root || "/";

    /* Landing after a bounce off /cart — open the drawer and clear the flag. */
    try {
      if (sessionStorage.getItem("tact-open-cart") === "1") {
        sessionStorage.removeItem("tact-open-cart");
        this.openCart(true);
      }
    } catch (_) {}

    if (!document.body.classList.contains("template-cart")) return;
    /* No drawer on the page means no replacement for it — leave /cart alone. */
    if (!this.cartDrawer) return;
    /* The merchant's choice, and the default is now the page.

       This method used to bounce every visit to /cart into the drawer without
       asking. The drawer is a good quick view and a poor review: a shopper who
       wants to check what they are about to pay for, change two quantities and
       drop one thing had the only surface built for that taken away from them,
       and any link to /cart — from an email, from Shopify's own notifications,
       from a bookmark — bounced them somewhere else. */
    if (this.cartDrawer.dataset.cartPageMode !== "drawer") return;

    document.documentElement.classList.add("t-cart-page-leaving");

    let back = root;
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      /* Never bounce back to /cart itself, and never off-site. */
      if (ref && ref.origin === window.location.origin && !/\/cart\/?$/.test(ref.pathname)) {
        back = ref.pathname + ref.search;
      }
    } catch (_) {}

    try { sessionStorage.setItem("tact-open-cart", "1"); } catch (_) {}
    /* replace, not assign, so /cart never enters the back history. */
    window.location.replace(back);
  }

  /* --------------------------------------------------------------------------
     Account sheet. The account link was a full page navigation, which is the
     least smooth thing a tap can do. It now opens a sheet that rises from the
     bottom edge; the link itself is untouched, so with JavaScript off the
     navigation still happens and nothing is lost.
     -------------------------------------------------------------------------- */
  bindCheckoutTracking() {
    if (this.checkoutTrackingBound) return;
    this.checkoutTrackingBound = true;
    document.addEventListener("click", (event) => {
      const hit = event.target.closest(
        '.t-cart-checkout__button, .gokwik-checkout button, [name="checkout"], .t-gokwik-buy',
      );
      if (!hit) return;
      this.fetchCart()
        .then((cart) => this.pixelInitiateCheckout(cart))
        .catch(() => {});
    }, true);
  }

  bindAccountSheet() {
    this.accountSheet = document.querySelector("[data-account-sheet]");
    if (!this.accountSheet || this.accountSheetBound) return;
    this.accountSheetBound = true;
    this.accountScrim = document.querySelector(".t-account-sheet__scrim");

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest('.t-header__account, [data-account-open], .t-mobile-dock a[href*="/account"]');
      if (trigger && !event.metaKey && !event.ctrlKey && event.button === 0) {
        event.preventDefault();
        this.openAccountSheet(trigger);
        return;
      }
      if (event.target.closest("[data-account-close]") || event.target.closest(".t-account-sheet__scrim")) {
        this.closeAccountSheet();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.accountSheet.classList.contains("is-open")) this.closeAccountSheet();
    });

    const form = this.accountSheet.querySelector("[data-account-form]");
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      this.submitAccountPhone(form);
    });
  }

  openAccountSheet(origin) {
    if (!this.accountSheet) return;
    this.closeCart();
    this.closeMenu();
    this.accountOpener = origin || null;
    this.accountSheet.hidden = false;
    this.accountScrim?.removeAttribute("hidden");
    /* One frame so the sheet has a resting position to animate away from. */
    window.requestAnimationFrame(() => {
      this.accountSheet.classList.add("is-open");
      this.accountSheet.setAttribute("aria-hidden", "false");
      this.accountScrim?.classList.add("is-open");
      if (this.motionOk) {
        this.M.animate(this.accountSheet, { transform: ["translateY(100%)", "translateY(0)"] }, this.spring.sheet);
      }
      this.accountSheet.querySelector("[data-account-phone]")?.focus({ preventScroll: true });
    });
    this.lockPageScroll();
  }

  closeAccountSheet() {
    if (!this.accountSheet || !this.accountSheet.classList.contains("is-open")) return;
    this.accountSheet.classList.remove("is-open");
    this.accountSheet.setAttribute("aria-hidden", "true");
    this.accountScrim?.classList.remove("is-open");
    if (this.motionOk) {
      this.M.animate(this.accountSheet, { transform: ["translateY(0)", "translateY(100%)"] }, {
        ...this.spring.sheet,
        stiffness: this.spring.sheet.stiffness + 90,
      });
    }
    window.setTimeout(() => {
      if (this.accountSheet.classList.contains("is-open")) return;
      this.accountSheet.hidden = true;
      this.accountScrim?.setAttribute("hidden", "");
      this.unlockPageScroll();
    }, this.motionOk ? 420 : 200);
    this.accountOpener?.focus({ preventScroll: true });
    this.accountOpener = null;
  }

  /* Phone sign-in belongs to GoKwik's kwikpass, which is an app embed rather
     than theme code and may not be enabled. Hand off when it is there; say
     plainly what is happening when it is not, instead of faking an OTP. */
  submitAccountPhone(form) {
    const input = form.querySelector("[data-account-phone]");
    const error = form.querySelector("[data-account-error]");
    const digits = (input?.value || "").replace(/\D/g, "");
    const show = (message) => {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    };
    if (error) error.hidden = true;

    if (digits.length !== 10) {
      show("Enter a 10-digit mobile number.");
      input?.focus();
      return;
    }

    /* kwikpass exposes itself differently depending on how the embed loads, so
       try the documented shapes in order rather than assuming one. */
    const kp = window.kwikpass || window.KwikPass || window.gokwikKwikpass || window.kwikPass;
    if (kp) {
      const open = kp.open || kp.openLogin || kp.login || kp.show;
      if (typeof open === "function") {
        open.call(kp, { phone: `+91${digits}`, phoneNumber: digits });
        return;
      }
    }
    for (const fn of ["openKwikPass", "kpOpenLogin", "showKwikpassModal"]) {
      if (typeof window[fn] === "function") {
        window[fn]({ phone: `+91${digits}` });
        return;
      }
    }
    const trigger = document.querySelector(
      "#kp_login, #kwikpass-login, [data-kwikpass-login], .kwikpass-login, .kp-login-btn",
    );
    if (trigger) {
      trigger.click();
      return;
    }

    /* No kwikpass on this store — send them to Shopify's own login rather than
       leaving the button dead. */
    show("Phone sign-in is not enabled yet — opening the full login.");
    window.setTimeout(() => {
      window.location.assign(document.body.dataset.accountLoginUrl || "/account/login");
    }, 900);
  }

  /* Meta events that only the theme can see. Purchase is deliberately absent —
     Shopify's checkout and GoKwik's both run on other origins, so the theme
     cannot observe a completed order at all; that event has to come from the
     Meta sales channel or GoKwik. */
  trackPixel(name, params) {
    try { window.tactPixel?.track(name, params); } catch (_) {}
  }

  /* Shopify's cart payload is the trustworthy source for what was actually
     added — the form can lie about quantity when a line already exists. */
  pixelAddToCart(added) {
    if (!added) return;
    const items = added.items || [added];
    const first = items[0];
    if (!first) return;
    this.trackPixel("AddToCart", {
      content_ids: items.map((i) => String(i.variant_id ?? i.id)).filter(Boolean),
      content_type: "product_variant",
      content_name: first.product_title || first.title,
      value: (items.reduce((sum, i) => sum + (i.final_line_price ?? i.line_price ?? 0), 0)) / 100,
      currency: this.cartCurrency || "INR",
      num_items: items.reduce((sum, i) => sum + (i.quantity || 1), 0),
    });
  }

  pixelInitiateCheckout(cart) {
    if (!cart) return;
    this.trackPixel("InitiateCheckout", {
      content_ids: (cart.items || []).map((i) => String(i.variant_id ?? i.id)),
      content_type: "product_variant",
      value: (cart.total_price || 0) / 100,
      currency: cart.currency || this.cartCurrency || "INR",
      num_items: cart.item_count || 0,
    });
  }

  /* --------------------------------------------------------------------------
     Predictive search. The icon used to navigate to /search, which is a full
     page load before the shopper has typed a character. This opens over the
     page with products already showing, and narrows as they type.
     -------------------------------------------------------------------------- */
  bindSearch() {
    this.searchDrawer = document.querySelector("[data-search-drawer]");
    if (!this.searchDrawer || this.searchBound) return;
    this.searchBound = true;
    this.searchScrim = document.querySelector(".t-search__scrim");
    this.searchInput = this.searchDrawer.querySelector("[data-search-input]");

    document.addEventListener("click", (event) => {
      const opener = event.target.closest('.t-header__search, [data-search-open], .t-mobile-dock a[href*="/search"]');
      if (opener && !event.metaKey && !event.ctrlKey && event.button === 0) {
        event.preventDefault();
        this.openSearch();
        return;
      }
      if (event.target.closest("[data-search-close]") || event.target.closest(".t-search__scrim")) {
        this.closeSearch();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.searchDrawer.classList.contains("is-open")) this.closeSearch();
    });

    let debounce;
    this.searchInput?.addEventListener("input", () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => this.runSearch(this.searchInput.value.trim()), 220);
    });
  }

  openSearch() {
    if (!this.searchDrawer) return;
    this.closeCart();
    this.closeMenu();
    this.searchDrawer.hidden = false;
    this.searchScrim?.removeAttribute("hidden");
    window.requestAnimationFrame(() => {
      this.searchDrawer.classList.add("is-open");
      this.searchDrawer.setAttribute("aria-hidden", "false");
      this.searchScrim?.classList.add("is-open");
      /* Focus after the panel has somewhere to be, or iOS scrolls the page. */
      this.searchInput?.focus({ preventScroll: true });
    });
    this.lockPageScroll();
  }

  closeSearch() {
    if (!this.searchDrawer?.classList.contains("is-open")) return;
    this.searchDrawer.classList.remove("is-open");
    this.searchDrawer.setAttribute("aria-hidden", "true");
    this.searchScrim?.classList.remove("is-open");
    window.setTimeout(() => {
      if (this.searchDrawer.classList.contains("is-open")) return;
      this.searchDrawer.hidden = true;
      this.searchScrim?.setAttribute("hidden", "");
      this.unlockPageScroll();
    }, this.motionOk ? 380 : 180);
  }

  async runSearch(term) {
    const defaults = this.searchDrawer.querySelector("[data-search-default]");
    const results = this.searchDrawer.querySelector("[data-search-results]");
    const grid = this.searchDrawer.querySelector("[data-search-results-grid]");
    const heading = this.searchDrawer.querySelector("[data-search-results-heading]");
    const all = this.searchDrawer.querySelector("[data-search-all]");
    const status = this.searchDrawer.querySelector("[data-search-status]");
    if (!results || !grid) return;

    /* Nothing typed: fall back to the merchandised set rather than an empty
       panel. An empty search should still be shoppable. */
    if (term.length < 2) {
      results.hidden = true;
      if (defaults) defaults.hidden = false;
      if (status) status.hidden = true;
      return;
    }

    /* Guard against a slow response for an old term landing after a newer one. */
    const token = (this.searchToken = (this.searchToken || 0) + 1);

    try {
      const url = new URL(`${window.Shopify?.routes?.root || "/"}search/suggest.json`, window.location.origin);
      url.searchParams.set("q", term);
      url.searchParams.set("resources[type]", "product");
      url.searchParams.set("resources[limit]", "8");
      url.searchParams.set("resources[options][unavailable_products]", "last");
      const response = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("search unavailable");
      const payload = await response.json();
      if (token !== this.searchToken) return;

      const products = payload?.resources?.results?.products || [];
      if (defaults) defaults.hidden = true;
      results.hidden = false;
      if (heading) heading.textContent = `${products.length} result${products.length === 1 ? "" : "s"} for “${term}”`;
      grid.innerHTML = "";

      if (!products.length) {
        if (status) {
          status.textContent = `Nothing matches “${term}”. Try a shorter word.`;
          status.hidden = false;
        }
        if (all) all.hidden = true;
        return;
      }
      if (status) status.hidden = true;

      for (const product of products) {
        const card = document.createElement("a");
        card.className = "t-search__card";
        card.href = this.safeStoreUrl(product.url) || "#";
        if (product.featured_image?.url) {
          const image = document.createElement("img");
          image.src = product.featured_image.url;
          image.alt = product.title || "";
          image.loading = "lazy";
          card.append(image);
        } else {
          const blank = document.createElement("span");
          blank.className = "t-search__card-blank";
          card.append(blank);
        }
        const copy = document.createElement("span");
        copy.className = "t-search__card-copy";
        const title = document.createElement("strong");
        title.textContent = product.title || "";
        const price = document.createElement("em");
        price.textContent = product.price ? `Rs. ${product.price}` : "";
        copy.append(title, price);
        card.append(copy);
        grid.append(card);
      }

      if (all) {
        const target = new URL(`${window.Shopify?.routes?.root || "/"}search`, window.location.origin);
        target.searchParams.set("q", term);
        all.href = target.pathname + target.search;
        all.hidden = false;
      }
    } catch (_) {
      if (token !== this.searchToken) return;
      if (status) {
        status.textContent = "Search is unavailable right now.";
        status.hidden = false;
      }
    }
  }

  /* Overlay scroll lock.

     `body { overflow: hidden }` on its own does not hold on iOS Safari or on
     Chrome for Android: the viewport keeps scrolling under the drawer, which is
     why a swipe inside the cart moved the page behind it. Pinning the body with
     position:fixed at its current offset is the only technique those browsers
     honour, so the scroll position is stashed on the way in and restored on the
     way out.

     Whether the page stays locked is recomputed from the DOM rather than kept
     as a counter: several callers already close conditionally, so a counter
     would drift out of step and strand the page in a locked state. */
  /* The scroll lock's dead-man's handle.

     `body.t-overlay-open` sets `overflow: hidden`, and `unlockPageScroll`
     refuses to lift it while `anyOverlayOpen()` says something is still up. So
     a single panel that keeps `is-open` after it has visually closed freezes
     the entire page until a reload — the comment in `anyOverlayOpen` records
     this happening once already, and a shopper who cannot scroll cannot buy.

     This does not trust the class. A slide-in panel that is closed sits
     translated outside the viewport, so a panel only counts as open if it is
     actually overlapping the screen. When the lock is on and nothing overlaps,
     the lock is wrong and is lifted. */
  releaseStuckScrollLock() {
    const root = document.documentElement;
    if (!root.classList.contains("t-overlay-open")) return;

    /* A panel slides in over 520ms, and this runs on every click in the capture
       phase — so a shopper who taps inside a drawer the moment it appears is
       asking "is anything on screen?" while the drawer is still travelling.
       The honest answer then is "not yet", and the lock was being torn off
       underneath a drawer that was in the middle of opening. A lock that was
       taken moments ago is not a stuck lock; only an old one can be. */
    if (performance.now() - (this.lockedAt || 0) < 900) return;

    const panels = [
      this.cartDrawer,
      this.menu,
      this.wishlist,
      this.sizeGuide,
      this.quickView,
      this.accountSheet,
      this.searchDrawer,
      this.lookSheet,
      document.querySelector("[data-size-guide-shared]"),
    ];

    const onScreen = panels.some((panel) => {
      if (!panel || !panel.classList.contains("is-open")) return false;
      const box = panel.getBoundingClientRect();
      if (!box.width || !box.height) return false;
      /* Overlapping the viewport, not merely present in the document. */
      return box.left < window.innerWidth - 8 && box.right > 8;
    });

    if (onScreen) return;

    /* body.style.top is the offset the lock applied, and it is authoritative
       even if lockedScrollY has drifted. Clearing it without scrolling back
       drops the page at 0 — releasing the lock is meant to be invisible, not
       to throw the shopper to the top. */
    const offset = Math.abs(parseInt(document.body.style.top || "0", 10)) || this.lockedScrollY || 0;
    root.classList.remove("t-overlay-open");
    document.body.classList.remove("t-overlay-open");
    document.body.style.top = "";
    if (offset) window.scrollTo({ top: offset, left: 0, behavior: "instant" });
    this.lockedScrollY = 0;
  }

  bindScrollLockGuard() {
    const release = () => this.releaseStuckScrollLock();
    window.addEventListener("load", release);
    /* Coming back through the bfcache restores the old DOM, lock and all. */
    window.addEventListener("pageshow", release);
    document.addEventListener("visibilitychange", release);
    /* Two things a stuck shopper will try unprompted. */
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") release();
    });
    document.addEventListener("click", release, true);
  }

  anyOverlayOpen() {
    return Boolean(
      this.cartDrawer?.classList.contains("is-open") ||
      this.menu?.classList.contains("is-open") ||
      this.wishlist?.classList.contains("is-open") ||
      this.sizeGuide?.classList.contains("is-open") ||
      /* `is-open` and not `hidden`: hidden is only set when the close
         animation finishes, 420ms later. Reading it here meant a close that
         called unlockPageScroll() synchronously always saw the quick view as
         still open, bailed out, and left `body { position: fixed }` on the
         document — the page frozen until a reload. Every flag in this list
         must be one that close() clears synchronously. */
      this.quickView?.classList.contains("is-open") ||
      document.querySelector("[data-size-guide-shared]")?.classList.contains("is-open") ||
      this.accountSheet?.classList.contains("is-open") ||
      this.searchDrawer?.classList.contains("is-open") ||
      /* Shop the look. openLookSheet() locks the page but this list did not
         know about it, so any *other* overlay closing while the sheet was up
         unlocked the body underneath it and threw the saved scroll position
         away. Everything after that — closing the sheet, picking an item,
         opening the quick view — restored to 0 and jumped the shopper to the
         top of the page. Every overlay that locks has to be listed here. */
      this.lookSheet?.classList.contains("is-open")
    );
  }

  lockPageScroll() {
    /* Heal a desync: the class can survive a close that bailed, and if the
       body is no longer actually offset then the saved position is stale.
       Re-capture rather than trust it. */
    if (document.documentElement.classList.contains("t-overlay-open")) {
      if (!document.body.style.top) {
        this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = `-${this.lockedScrollY}px`;
      }
      return;
    }
    this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${this.lockedScrollY}px`;
    document.documentElement.classList.add("t-overlay-open");
    document.body.classList.add("t-overlay-open");
    this.lockedAt = performance.now();
  }

  unlockPageScroll() {
    if (this.anyOverlayOpen()) return;
    if (!document.documentElement.classList.contains("t-overlay-open")) return;
    document.documentElement.classList.remove("t-overlay-open");
    document.body.classList.remove("t-overlay-open");
    document.body.style.top = "";
    /* base.css sets scroll-behavior: smooth on html, so a plain scrollTo here
       animates the page back to where it was — the whole document visibly flies
       past as the drawer closes. The restore has to be instant. */
    window.scrollTo({ top: this.lockedScrollY || 0, left: 0, behavior: "instant" });
    this.lockedScrollY = 0;
  }

  async openCart(refresh = false) {
    if (!this.cartDrawer) return;
    /* Before it is seen, not after. */
    this.ensureCheckoutClickable();
    /* Kwik Cart installed: it opens itself after an add. Opening ours as well
       would stack two carts. */
    if (this.gokwikCartPresent()) return;
    /* Flagged open before the hand-off, for the reason spelled out in
       openWishlist(): the lock must never see an empty room mid-swap. */
    this.cartDrawer.classList.add("is-open");
    this.cartDrawer.setAttribute("aria-hidden", "false");
    this.closeMenu();
    this.closeWishlist();
    this.header?.classList.remove("is-hidden");
    this.cartOverlay?.classList.add("is-open");
    this.animateDrawer(this.cartDrawer, this.cartOverlay, true, { axis: "x", closed: "104%" });
    this.lockPageScroll();
    if (this.motionOk) {
      const lines = this.cartDrawer.querySelectorAll(".t-cart-drawer__line");
      if (lines.length) this.M.animate(lines, { opacity: [0, 1], x: [16, 0] }, { ...this.spring.soft, delay: this.M.stagger(0.04) });
    }
    if (!refresh) return;
    this.cartDrawer.classList.add("is-busy");
    try {
      this.syncCartDrawer(await this.fetchCart());
    } catch {
      window.location.assign(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : "/cart");
    } finally {
      this.cartDrawer.classList.remove("is-busy");
    }
  }

  closeCart() {
    if (!this.cartDrawer) return;
    /* Closing a drawer that is already closed is not free: it replays the exit
       animation from its open state, which shows the panel for a frame, and it
       drops the page's scroll lock only for the next drawer to take it again.
       Opening the wishlist calls this unconditionally, which is why the cart
       flashed past on the way. */
    if (!this.cartDrawer.classList.contains("is-open")) return;
    this.cartDrawer.classList.remove("is-open");
    this.cartDrawer.setAttribute("aria-hidden", "true");
    this.cartOverlay?.classList.remove("is-open");
    this.animateDrawer(this.cartDrawer, this.cartOverlay, false, { axis: "x", closed: "104%" });
    /* anyOverlayOpen() decides this, and it is the only thing that knows the
       whole list. The hand-rolled test here named two panels out of nine. */
    this.unlockPageScroll();
  }

  /* One request per line, sent after the tapping stops. Without this a shopper
     going from 1 to 4 pays for four round trips and four re-renders. */
  queueCartLine(key, quantity) {
    if (!key) return;
    this.cartQueue = this.cartQueue || new Map();
    window.clearTimeout(this.cartQueue.get(key));
    this.cartQueue.set(
      key,
      window.setTimeout(() => {
        this.cartQueue.delete(key);
        this.changeCartLine(key, quantity);
      }, 260),
    );
  }

  /* The cart page is rendered markup, not a drawer the script owns: changing a
     line has to redraw the line prices, the subtotal, the savings and the
     reward ladder, none of which `syncCartDrawer` touches. Shopify's Section
     Rendering API returns the section exactly as it would render now, so the
     page catches up without a reload. If that request is the thing that fails,
     the change has already happened on the server and a reload is the honest
     way to show it. */
  async refreshCartPage() {
    const page = document.querySelector("[data-cart-page]");
    if (!page) return;
    page.classList.add("is-busy");
    try {
      const response = await fetch(`${this.shopifyPath("cart")}?section_id=main`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!response.ok) throw new Error(String(response.status));
      const markup = await response.text();
      const next = new DOMParser().parseFromString(markup, "text/html").querySelector("[data-cart-page]");
      if (!next) throw new Error("cart section missing");
      page.replaceWith(next);
    } catch (_) {
      window.location.reload();
    }
  }

  async changeCartLine(key, quantity) {
    /* The drawer is no longer required. It used to be, and on a store with the
       drawer switched off that made every stepper and every remove control on
       the cart page do nothing at all — silently, because the guard returned
       before the request. The cart is the cart whether or not a panel exists to
       show it. */
    if (!key) return;
    this.cartDrawer?.classList.add("is-busy");
    this.cartDrawer?.querySelector("[data-cart-drawer-footer]")?.classList.add("is-settling");
    try {
      const cart = await this.requestJson("cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      this.syncCartDrawer(cart);
      await this.refreshCartPage();
      document.dispatchEvent(new CustomEvent("tact:cart-changed", { detail: cart }));
    } finally {
      this.cartDrawer?.classList.remove("is-busy");
      this.cartDrawer?.querySelector("[data-cart-drawer-footer]")?.classList.remove("is-settling");
      document
        .querySelectorAll("[data-cart-line].is-pending")
        .forEach((node) => node.classList.remove("is-pending", "is-removing"));
    }
  }

  /* Quick view drawer.
     Contents come from sections/quick-view.liquid through the Section
     Rendering API, so sizes, prices, availability and the Buy it now button
     are real Liquid rather than a JS reconstruction. Only variant switching,
     focus handling and the add request live here. */
  bindQuickView() {
    if (this.quickViewBound) return;
    this.quickViewBound = true;
    this.quickView = document.querySelector("[data-quick-view]");
    if (!this.quickView) return;
    this.quickViewBody = this.quickView.querySelector("[data-quick-view-body]");
    this.quickViewPanel = this.quickView.querySelector(".qv-drawer__panel");
    this.quickViewOpener = null;
    this.quickViewToken = 0;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-quick-view-open]");
      if (trigger) {
        event.preventDefault();
        this.openQuickView(trigger);
        return;
      }
      if (event.target.closest("[data-quick-view-close]")) {
        event.preventDefault();
        this.closeQuickView();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (!this.quickView || this.quickView.hidden) return;
      if (event.key === "Escape") {
        this.closeQuickView();
        return;
      }
      if (event.key === "Tab") this.trapQuickViewFocus(event);
    });

    /* Option changes and the quantity stepper are delegated, because the
       drawer's markup is replaced on every open. */
    this.quickView.addEventListener("change", (event) => {
      if (event.target.closest("[data-qv-option-input]")) this.syncQuickViewVariant();
    });

    this.quickView.addEventListener("click", (event) => {
      const step = event.target.closest("[data-qv-quantity]");
      if (!step) return;
      const input = this.quickView.querySelector("[data-qv-quantity-input]");
      if (!input) return;
      const next = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qvQuantity));
      input.value = String(next);
    });

    this.quickView.addEventListener("submit", (event) => {
      const form = event.target.closest(".qv__form");
      if (!form) return;
      event.preventDefault();
      this.submitQuickView(form);
    });
  }

  openQuickView(trigger) {
    return this.openQuickViewFor(trigger?.dataset?.productUrl, trigger);
  }

  async openQuickViewFor(url, origin) {
    if (!url || !this.quickView) return;
    this.quickViewOpener = origin || null;
    const token = ++this.quickViewToken;

    this.quickView.hidden = false;
    this.quickViewPanel?.setAttribute("aria-busy", "true");
    if (this.quickViewBody) this.quickViewBody.innerHTML = "";
    /* Force a frame so the opening transition runs from the closed state. */
    void this.quickView.offsetWidth;
    this.quickView.classList.add("is-open");

    /* One drawer at a time. Choosing a size from inside the bag used to stack
       the quick view on top of the open cart — two panels, two scrims, and no
       way to tell which one a tap belonged to. The bag steps aside and the
       size picker takes the screen; adding from it re-opens the bag with the
       new line, so the shopper ends where they started.

       Flagged open above before closing the cart, so anyOverlayOpen() always
       sees a drawer and the page never unlocks mid-hand-off. */
    this.closeCart();
    this.closeMenu();

    this.quickViewAxis = window.matchMedia("(max-width: 749px)").matches ? "y" : "x";
    this.animateDrawer(this.quickViewPanel, this.quickView.querySelector(".qv-drawer__scrim"), true, {
      axis: this.quickViewAxis,
      closed: "101%",
    });
    this.lockPageScroll();

    try {
      const response = await fetch(`${this.safeStoreUrl(url)}?section_id=quick-view`, {
        headers: { Accept: "text/html" },
      });
      if (!response.ok) throw new Error("Quick view is unavailable.");
      const markup = await response.text();
      if (token !== this.quickViewToken) return;
      const parsed = new DOMParser().parseFromString(markup, "text/html");
      const content = parsed.querySelector("[data-quick-view-root]");
      if (!content) throw new Error("Quick view is unavailable.");
      if (this.quickViewBody) this.quickViewBody.replaceChildren(content);
      this.releaseGokwikIn(this.quickViewBody);
      this.syncQuickViewVariant();
      this.bindQuickGalleries(this.quickView);
      if (this.motionOk) {
        const rows = this.quickView.querySelectorAll(".qv__panel > *");
        this.M.animate(rows, { opacity: [0, 1], y: [10, 0] }, { ...this.spring.soft, delay: this.M.stagger(0.035, { start: 0.05 }) });
      }
      const focusTarget =
        this.quickView.querySelector("[data-qv-option-input]:not([disabled])") ||
        this.quickView.querySelector("[data-qv-add]") ||
        this.quickView.querySelector("[data-quick-view-close]");
      focusTarget?.focus({ preventScroll: true });
    } catch {
      if (token !== this.quickViewToken) return;
      /* No dead end: send the shopper to the product page instead. */
      this.closeQuickView();
      window.location.assign(this.safeStoreUrl(url));
    } finally {
      if (token === this.quickViewToken) this.quickViewPanel?.setAttribute("aria-busy", "false");
    }
  }

  closeQuickView() {
    if (!this.quickView || this.quickView.hidden) return;
    this.quickViewToken += 1;
    this.quickView.classList.remove("is-open");
    this.animateDrawer(this.quickViewPanel, this.quickView.querySelector(".qv-drawer__scrim"), false, {
      axis: this.quickViewAxis || "x",
      closed: "101%",
    });
    this.unlockPageScroll();
    const restore = this.quickViewOpener;
    this.quickViewOpener = null;
    this.restoreQuickViewOrigin();
    const finish = () => {
      if (this.quickView.classList.contains("is-open")) return;
      this.quickView.hidden = true;
      if (this.quickViewBody) this.quickViewBody.innerHTML = "";
      /* Belt and braces: a stuck scroll lock takes the whole storefront down,
         so re-run the check once every overlay has actually settled. */
      this.unlockPageScroll();
    };
    window.setTimeout(finish, this.motionOk ? 420 : 240);
    restore?.focus({ preventScroll: true });
  }

  trapQuickViewFocus(event) {
    const focusables = this.quickView.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  quickViewVariants() {
    const node = this.quickView?.querySelector("[data-quick-view-variants]");
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  syncQuickViewVariant() {
    if (!this.quickView) return;
    const fieldsets = Array.from(this.quickView.querySelectorAll("[data-qv-option-index]"));
    const idInput = this.quickView.querySelector("[data-qv-variant-id]");
    const addButton = this.quickView.querySelector("[data-qv-add]");
    const addLabel = this.quickView.querySelector("[data-qv-add-label]");
    if (!idInput) return;

    /* No option groups means a single-variant product; the hidden id stands. */
    if (!fieldsets.length) return;

    const chosen = fieldsets.map((set) => {
      const picked = set.querySelector("[data-qv-option-input]:checked");
      const head = set.querySelector("[data-qv-option-value]");
      const value = picked ? picked.value : "";
      if (head) head.textContent = value;
      return value;
    });

    const variants = this.quickViewVariants();
    const match = variants.find((variant) =>
      chosen.every((value, index) => String(variant[`option${index + 1}`] ?? "") === value)
    );

    if (match) idInput.value = String(match.id);

    const available = Boolean(match && match.available);
    if (addButton) addButton.disabled = !available;
    if (addLabel) {
      addLabel.textContent = available
        ? addLabel.dataset.restLabel || addLabel.textContent
        : "Sold out";
    }
    if (addLabel && available && !addLabel.dataset.restLabel) {
      addLabel.dataset.restLabel = addLabel.textContent;
    }

    const current = this.quickView.querySelector("[data-qv-current]");
    const compare = this.quickView.querySelector("[data-qv-compare]");
    const saving = this.quickView.querySelector("[data-qv-saving]");
    if (match && current) {
      const money = (cents) => this.formatQuickViewMoney(cents);
      current.textContent = money(match.price);
      if (compare) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compare.textContent = money(match.compare_at_price);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }
      if (saving) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          const percent = Math.round(((match.compare_at_price - match.price) / match.compare_at_price) * 100);
          saving.textContent = `Save ${percent}%`;
          saving.hidden = false;
        } else {
          saving.hidden = true;
        }
      }
    }
  }

  formatQuickViewMoney(cents) {
    const currency = window.Shopify?.currency?.active || "INR";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(Number(cents || 0) / 100);
    } catch {
      return String(Number(cents || 0) / 100);
    }
  }

  async submitQuickView(form) {
    const button = form.querySelector("[data-qv-add]");
    const label = form.querySelector("[data-qv-add-label]");
    const status = form.querySelector("[data-qv-status]");
    if (button?.disabled) return;
    const resting = label?.textContent || "Add to cart";

    const setState = (state, text) => {
      button?.classList.remove("is-loading", "is-added", "is-error");
      if (state) button?.classList.add(state);
      if (label) label.textContent = text;
      if (status) status.textContent = text;
    };

    setState("is-loading", "Adding…");
    button?.setAttribute("disabled", "disabled");

    try {
      const body = new FormData(form);
      const cart = await this.requestJson("cart/add.js", { method: "POST", body });
      this.pixelAddToCart(cart);
      setState("is-added", "Added");
      if (this.motionOk && button) {
        this.M.animate(button, { scale: [1, 0.96, 1] }, { duration: 0.4, easing: [0.34, 1.56, 0.64, 1] });
      }
      const fresh = await this.fetchCart().catch(() => cart);
      document.dispatchEvent(new CustomEvent("tact:cart-updated", { detail: { cart: fresh, open: true } }));
      window.setTimeout(() => {
        this.closeQuickView();
        setState(null, resting);
        button?.removeAttribute("disabled");
      }, 650);
    } catch (error) {
      setState("is-error", this.friendlyCartError ? this.friendlyCartError(error) : "Could not add");
      window.setTimeout(() => {
        setState(null, resting);
        button?.removeAttribute("disabled");
      }, 2200);
    }
  }

  /* The drawer's reward ladder, savings line, compare-at prices and totals are
     all computed in Liquid. Rebuilding them by hand in JS would drift, and the
     Ajax cart payload does not even carry compare_at_price. So paint the cheap
     parts immediately for responsiveness, then let the server re-render the
     drawer for correctness. Every cart handler is delegated on document, and
     this replaces innerHTML rather than the element itself, so nothing unbinds. */
  async syncCartDrawer(cart) {
    this.renderCartDrawer(cart);
    if (!this.cartDrawer) return;
    try {
      const response = await fetch(`${this.shopifyPath("")}?sections=header`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const payload = await response.json();
      const markup = payload?.header;
      if (typeof markup !== "string" || !markup.length) return;
      const parsed = new DOMParser().parseFromString(markup, "text/html");
      const fresh = parsed.querySelector("[data-cart-drawer]");
      if (!fresh) return;

      /* The drawer is re-rendered whole, so a line that has just been added
         arrives in the same frame as every line that was already there — the
         one moment a shopper is actually looking for confirmation is a hard
         cut. Note which keys existed before the swap, and let only the genuinely
         new ones announce themselves. Animating all of them on every sync would
         be the opposite of the point: a list that reshuffles itself each time
         the quantity changes. */
      const before = new Set(
        Array.from(this.cartDrawer.querySelectorAll("[data-cart-line]"), (line) => line.dataset.cartLine),
      );
      this.cartDrawer.innerHTML = fresh.innerHTML;
      if (before.size) {
        this.cartDrawer.querySelectorAll("[data-cart-line]").forEach((line) => {
          if (!before.has(line.dataset.cartLine)) line.classList.add("is-new");
        });
      }

      /* The swap replaces the checkout button with a fresh disabled one. */
      this.ensureCheckoutClickable();
    } catch {
      /* Keep the optimistic render. */
    }
  }

  renderCartDrawer(cart) {
    if (!this.cartDrawer || !cart) return;
    const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || "en-IN", {
      style: "currency",
      currency: cart.currency || "INR",
      maximumFractionDigits: 0,
    }).format(Number(cents || 0) / 100);
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const cartLine = (item) => {
      const article = element("article", "t-cart-drawer__line");
      article.dataset.cartLine = String(item.key || "");

      const productUrl = this.safeStoreUrl(item.url);
      const mediaLink = element("a");
      mediaLink.href = productUrl;
      const imageUrl = this.safeImageUrl(item.image);
      if (imageUrl) {
        const image = element("img");
        const sizedImage = new URL(imageUrl);
        sizedImage.searchParams.set("width", "180");
        image.src = sizedImage.href;
        image.alt = String(item.product_title || "");
        image.loading = "lazy";
        mediaLink.append(image);
      }

      const details = element("div");
      const titleLink = element("a");
      titleLink.href = productUrl;
      titleLink.append(element("strong", "", String(item.product_title || "")));
      details.append(titleLink);
      if (item.variant_title && item.variant_title !== "Default Title") {
        details.append(element("small", "", String(item.variant_title)));
      }
      /* Unit price here too, so the optimistic render matches what the server
         sends back and the number does not change under the shopper. */
      const unitPrice = element("span", "t-cart-drawer__line-price", formatMoney(item.final_price ?? item.price));
      if ((item.quantity || 1) > 1) {
        const each = element("em", "t-cart-drawer__line-each", "each");
        unitPrice.append(each);
      }
      details.append(unitPrice);

      const quantity = element("div", "t-cart-drawer__quantity");
      const decrease = element("button", "", "−");
      decrease.type = "button";
      decrease.dataset.cartQuantity = "-1";
      decrease.dataset.cartKey = String(item.key || "");
      decrease.setAttribute("aria-label", "Reduce quantity");
      const amount = element("b", "", String(Math.max(0, Number(item.quantity) || 0)));
      const increase = element("button", "", "+");
      increase.type = "button";
      increase.dataset.cartQuantity = "1";
      increase.dataset.cartKey = String(item.key || "");
      increase.setAttribute("aria-label", "Increase quantity");
      quantity.append(decrease, amount, increase);
      details.append(quantity);

      const remove = element("button", "t-cart-drawer__remove", "×");
      remove.type = "button";
      remove.dataset.cartRemove = "";
      remove.dataset.cartKey = String(item.key || "");
      remove.setAttribute("aria-label", `Remove ${String(item.product_title || "item")}`);

      article.append(mediaLink, details, remove);
      return article;
    };
    const body = this.cartDrawer.querySelector("[data-cart-drawer-body]");
    const footer = this.cartDrawer.querySelector("[data-cart-drawer-footer]");
    const empty = this.cartDrawer.querySelector("[data-cart-empty-template]");

    this.cartDrawer.querySelectorAll("[data-cart-drawer-count]").forEach((count) => {
      count.textContent = `(${cart.item_count})`;
    });
    this.updateCartIndicators(cart);

    if (body) {
      body.replaceChildren();
      if (!cart.items?.length) {
        if (empty?.content) body.append(empty.content.cloneNode(true));
      } else {
        const lines = element("div", "t-cart-drawer__lines");
        cart.items.slice(0, 100).forEach((item) => lines.append(cartLine(item)));
        body.append(lines);
      }
    }
    if (footer) footer.hidden = cart.item_count === 0;
    /* Keep the suggestions rail up with an empty bag; only gate the discount
       field on having something to discount. */
    const secondary = this.cartDrawer.querySelector("[data-cart-secondary]");
    if (secondary) secondary.hidden = false;
    const coupon = this.cartDrawer.querySelector("[data-cart-discount]");
    if (coupon) coupon.hidden = cart.item_count === 0;
    const total = this.cartDrawer.querySelector("[data-cart-drawer-total]");
    if (total) total.textContent = formatMoney(cart.total_price);

    const target = Number(this.cartDrawer.dataset.freeShippingTarget || 0);
    const remaining = Math.max(0, target - Number(cart.total_price || 0));
    const progress = target > 0 ? Math.min(100, (Number(cart.total_price || 0) / target) * 100) : 100;
    const label = this.cartDrawer.querySelector("[data-cart-shipping-label]");
    const bar = this.cartDrawer.querySelector("[data-cart-shipping-progress]");
    if (label) {
      label.textContent = remaining > 0
        ? `${this.cartDrawer.dataset.shippingBefore || "Add"} ${formatMoney(remaining)} ${this.cartDrawer.dataset.shippingAfter || "for free shipping."}`
        : this.cartDrawer.dataset.shippingUnlocked || "Free shipping unlocked.";
    }
    /* scaleX, not width. Animating width makes the browser lay the page out
       and repaint on every frame of the fill; a transform is composited and
       touches neither. Same picture, a fraction of the work, and it no longer
       competes with the drawer opening at the same moment. */
    if (bar) bar.style.transform = `scaleX(${Math.max(0, Math.min(100, progress)) / 100})`;
  }

  bindWishlist() {
    const authenticated = document.body.dataset.customerAuthenticated === "true";
    const customerId = document.body.dataset.customerId || "guest";
    const storageKey = `tact-wishlist:${customerId}`;
    const guestKey = "tact-wishlist:guest";
    const pendingKey = "tact-wishlist-pending";

    /* Anything saved before signing in follows the shopper into their account,
       once, rather than appearing to have been lost. */
    if (authenticated && customerId !== "guest") {
      try {
        const guestRaw = window.localStorage.getItem(guestKey);
        if (guestRaw) {
          const mine = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
          const theirs = JSON.parse(guestRaw);
          if (Array.isArray(mine) && Array.isArray(theirs)) {
            const seen = new Set(mine.map((i) => i && i.handle));
            const merged = mine.concat(theirs.filter((i) => i && !seen.has(i.handle)));
            window.localStorage.setItem(storageKey, JSON.stringify(merged.slice(0, 48)));
          }
          window.localStorage.removeItem(guestKey);
        }
      } catch {}
    }
    /* Saving works for a guest. The list is browser storage either way, so
       requiring a login bought the shopper nothing and cost them the save —
       and a shopper signed in through Kwikpass reads as a guest here, because
       that is a different session from Shopify's. */
    const read = () => {
      try {
        const raw = window.localStorage.getItem(storageKey) || "[]";
        if (raw.length > 100000) return [];
        const stored = JSON.parse(raw);
        return Array.isArray(stored)
          ? stored.map((item) => this.normalizeWishlistItem(item)).filter(Boolean).slice(0, 48)
          : [];
      } catch {
        return [];
      }
    };
    const write = (items) => {
      try {
        const safeItems = items
          .map((item) => this.normalizeWishlistItem(item))
          .filter(Boolean)
          .slice(0, 48);
        window.localStorage.setItem(storageKey, JSON.stringify(safeItems));
      } catch {}
    };
    const wishlistItem = (item) => {
      const article = document.createElement("article");
      article.className = "t-wishlist__item";
      const link = document.createElement("a");
      link.href = item.url;
      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = "";
        image.loading = "lazy";
        link.append(image);
      }
      const copy = document.createElement("span");
      const title = document.createElement("strong");
      title.textContent = item.title;
      const price = document.createElement("small");
      price.textContent = item.price;
      copy.append(title, price);
      link.append(copy);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.dataset.wishlistRemove = item.handle;
      remove.setAttribute("aria-label", `Remove ${item.title || "item"}`);
      const icon = document.createElement("span");
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = "×";
      remove.append(icon);
      article.append(link, remove);
      return article;
    };
    const render = () => {
      const items = read();
      document.querySelectorAll("[data-wishlist-count]").forEach((count) => {
        count.textContent = items.length;
        count.hidden = items.length === 0;
      });
      document.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
        const saved = items.some((item) => item.handle === button.dataset.wishlistHandle);
        button.setAttribute("aria-pressed", String(saved));
      });
      document.querySelectorAll("[data-wishlist-items]").forEach((container) => {
        container.replaceChildren(...items.map(wishlistItem));
      });
      document.querySelectorAll("[data-wishlist-empty]").forEach((empty) => {
        empty.hidden = items.length > 0;
      });
    };

    if (authenticated) {
      try {
        const pendingRaw = window.localStorage.getItem(pendingKey) || "";
        const pending = pendingRaw.length < 10000
          ? this.normalizeWishlistItem(JSON.parse(pendingRaw || "null"))
          : null;
        if (pending) {
          const items = read();
          if (!items.some((item) => item.handle === pending.handle)) items.unshift(pending);
          write(items.slice(0, 48));
          window.localStorage.removeItem(pendingKey);
        }
      } catch {
        window.localStorage.removeItem(pendingKey);
      }
    }

    if (!document.body.dataset.wishlistBound) {
      document.body.dataset.wishlistBound = "true";
      document.addEventListener("click", (event) => {
        const toggle = event.target.closest("[data-wishlist-toggle]");
        if (toggle) {
          event.preventDefault();
          /* No login check. This used to stash the item as "pending", open the
             drawer on a sign-in wall and return without saving — which is what
             a Kwikpass-authenticated shopper hit, because Kwikpass is not a
             Shopify customer session. The save just happens now. */
          const items = read();
          const candidate = this.normalizeWishlistItem({
            handle: toggle.dataset.wishlistHandle,
            title: toggle.dataset.wishlistTitle || "",
            url: toggle.dataset.wishlistUrl || "#",
            image: toggle.dataset.wishlistImage || "",
            price: toggle.dataset.wishlistPrice || "",
          });
          if (!candidate) return;
          const handle = candidate.handle;
          const index = items.findIndex((item) => item.handle === handle);
          if (index >= 0) items.splice(index, 1);
          else {
            items.unshift(candidate);
            const glyph = toggle.querySelector("svg") || toggle;
            if (this.motionOk) this.M.animate(glyph, { scale: [1, 1.3, 0.92, 1] }, { duration: 0.42, easing: [0.34, 1.56, 0.64, 1] });
          }
          write(items.slice(0, 48));
          render();
        }
        const remove = event.target.closest("[data-wishlist-remove]");
        if (remove) {
          write(read().filter((item) => item.handle !== remove.dataset.wishlistRemove));
          render();
        }
        if (event.target.closest("[data-wishlist-open]")) this.openWishlist();
        if (event.target.closest("[data-wishlist-close]")) this.closeWishlist();
      });
    }
    render();
  }

  /* Campaign film "shop this look". One + per film → a picker sheet listing
     every product in the look → the quick-view drawer for size. Replaces the
     per-product chips with inline sizes. */
  bindVideoLook() {
    if (this.videoLookBound) return;
    this.videoLookBound = true;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-video-look]");
      if (!trigger) return;
      event.preventDefault();
      this.press(trigger, 0.92);
      const source = trigger.closest(".t-video-story")?.querySelector("[data-look-products]");
      let items = [];
      try {
        items = JSON.parse(source?.textContent || "[]");
      } catch {
        items = [];
      }
      if (!Array.isArray(items) || !items.length) return;
      if (items.length === 1) {
        this.openQuickViewFor(this.safeStoreUrl(items[0].url), trigger);
        return;
      }
      this.openLookSheet(items, trigger);
    });
  }

  /* Editorial header: glyph-scramble on hover, and a hand-drawn marker stroke
     under the current nav item. Both are decoration — the links work without
     them, and neither runs when motion is off. */
  bindHeaderScramble() {
    if (this.scrambleBound) return;
    this.scrambleBound = true;
    const header = document.querySelector("[data-site-header]");
    if (!header || header.dataset.headerStyle !== "editorial") return;

    const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&/*+-";
    const scramble = (el) => {
      if (!this.motionOk || el.dataset.scrambling === "true") return;
      const final = el.dataset.scrambleText || el.textContent;
      el.dataset.scrambleText = final;
      el.dataset.scrambling = "true";
      const chars = final.split("");
      let frame = 0;
      const total = chars.length * 3 + 6;
      const tick = () => {
        const settled = Math.floor(frame / 3);
        el.textContent = chars
          .map((ch, index) => {
            if (index < settled || ch === " ") return ch;
            return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          })
          .join("");
        frame += 1;
        if (frame <= total) {
          window.requestAnimationFrame(tick);
        } else {
          el.textContent = final;
          el.dataset.scrambling = "false";
        }
      };
      window.requestAnimationFrame(tick);
    };

    header.querySelectorAll("[data-scramble]").forEach((el) => {
      const host = el.closest("a, button") || el;
      host.addEventListener("pointerenter", () => scramble(el));
      host.addEventListener("focus", () => scramble(el));
    });
  }

  bindHeaderMarker() {
    if (this.markerBound) return;
    this.markerBound = true;
    const header = document.querySelector("[data-site-header]");
    if (!header || header.dataset.headerStyle !== "editorial") return;
    const current = header.querySelector('.t-header__links a[aria-current="page"]');
    if (!current || current.querySelector(".t-header__marker")) return;

    const marker = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    marker.setAttribute("class", "t-header__marker");
    marker.setAttribute("viewBox", "0 0 120 12");
    marker.setAttribute("preserveAspectRatio", "none");
    marker.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M2 8c14-4 30 1 46-2 15-3 32 3 47-1 9-2 16 1 23-1");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "3");
    path.setAttribute("stroke-linecap", "round");
    marker.append(path);
    current.append(marker);

    if (this.motionOk) {
      const length = path.getTotalLength ? path.getTotalLength() : 140;
      path.style.strokeDasharray = String(length);
      this.M.animate(
        path,
        { strokeDashoffset: [length, 0] },
        { duration: 0.6, easing: [0.22, 1, 0.36, 1], delay: 0.25 },
      );
    }
  }

  ensureLookSheet() {
    if (this.lookSheet) return this.lookSheet;
    const sheet = document.createElement("div");
    sheet.className = "t-look-sheet";
    sheet.hidden = true;
    sheet.innerHTML =
      '<button class="t-look-sheet__scrim" type="button" data-look-close aria-label="Close"></button>' +
      '<aside class="t-look-sheet__panel" role="dialog" aria-modal="true" aria-label="Shop this look">' +
      '<header class="t-look-sheet__bar"><h2>Shop this look</h2>' +
      '<button class="t-look-sheet__close" type="button" data-look-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg></button></header>' +
      '<div class="t-look-sheet__list" data-look-list></div></aside>';
    document.body.append(sheet);
    sheet.addEventListener("click", (event) => {
      if (event.target.closest("[data-look-close]")) this.closeLookSheet();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.lookSheet && !this.lookSheet.hidden) this.closeLookSheet();
    });
    this.lookSheet = sheet;
    return sheet;
  }

  openLookSheet(items, origin) {
    const sheet = this.ensureLookSheet();
    const list = sheet.querySelector("[data-look-list]");
    this.lookSheetOrigin = origin || null;
    list.replaceChildren();
    items.forEach((item) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "t-look-sheet__item";
      if (item.available === false) row.classList.add("is-unavailable");
      const img = this.safeImageUrl(item.image);
      row.innerHTML =
        (img ? `<img src="${img}" alt="" loading="lazy">` : '<span class="t-look-sheet__noimg"></span>') +
        `<span class="t-look-sheet__meta"><b>${this.escapeHtml(item.title || "")}</b><i>${this.escapeHtml(item.price || "")}</i></span>` +
        '<span class="t-look-sheet__go" aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 6 6 6-6 6"/></svg></span>';
      row.addEventListener("click", () => {
        const url = this.safeStoreUrl(item.url);
        this.closeLookSheet();
        window.setTimeout(() => this.openQuickViewFor(url, this.lookSheetOrigin), this.motionOk ? 180 : 0);
      });
      list.append(row);
    });

    sheet.hidden = false;
    void sheet.offsetWidth;
    sheet.classList.add("is-open");
    this.lockPageScroll();
    this.animateDrawer(sheet.querySelector(".t-look-sheet__panel"), sheet.querySelector(".t-look-sheet__scrim"), true, { axis: "y", closed: "101%" });
    if (this.motionOk) {
      this.M.animate(list.querySelectorAll(".t-look-sheet__item"), { opacity: [0, 1], y: [12, 0] }, { ...this.spring.soft, delay: this.M.stagger(0.04, { start: 0.05 }) });
    }
    list.querySelector(".t-look-sheet__item")?.focus({ preventScroll: true });
  }

  closeLookSheet() {
    const sheet = this.lookSheet;
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove("is-open");
    this.animateDrawer(sheet.querySelector(".t-look-sheet__panel"), sheet.querySelector(".t-look-sheet__scrim"), false, { axis: "y", closed: "101%" });
    /* Ask unconditionally. anyOverlayOpen() is the single place that decides
       whether the page may move again, and it now knows about this sheet — the
       hand-rolled `quickView.hidden` test here was a second, staler opinion
       about the same question. */
    this.unlockPageScroll();
    this.lookSheetOrigin?.focus?.({ preventScroll: true });
    window.setTimeout(() => {
      if (!sheet.classList.contains("is-open")) sheet.hidden = true;
    }, this.motionOk ? 420 : 240);
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
  }

  /* The product film.

     A band of photographs that the shopper moves sideways: a trackpad swipe
     left or right, a horizontal wheel, shift-and-wheel, the two arrows, or the
     arrow keys. Scrolling *down* does what scrolling down should do — it moves
     the page — and never drags the photographs with it.

     The band is a real scroll container, so every one of those gestures is the
     browser's own and none of them had to be invented or intercepted. What is
     added is the wrap: the reel is doubled, and when the offset passes the
     width of one set it is rewound by exactly that width, onto the copy that is
     already sitting where the original just was. There is no last photograph.

     The frame nearest the middle is the subject; the rest sit back. */
  bindProductGalleryFocus(gallery, slides, layout) {
    gallery.dataset.galleryBound = "true";
    gallery.classList.add("is-focus-gallery", `is-${layout}`);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const wide = window.matchMedia("(min-width: 750px)");
    const driven = layout === "spotlight" && wide.matches;

    let track = null;
    let frames = slides;
    let looping = false;

    if (driven) {
      track = document.createElement("div");
      track.className = "t-spotlight-track";
      track.append(...slides);

      /* Three sets, not two.

         With two the reel could never wrap forwards: the track was 2 spans
         wide, so the furthest scrollLeft could ever reach was 2 spans minus the
         width of the window, and the forward test asked for the full 2 spans.
         It was unreachable, which is exactly the reported symptom — swipe to
         the last photograph and it stops.

         With three the scroll lives in the middle set and has a whole set of
         room either side, so both wraps are reachable and neither can be seen.
         Clones are hidden from assistive technology; the full view maps them
         back to the original they copy. */
      for (let pass = 0; pass < 2; pass += 1) {
        slides.forEach((slide) => {
          const copy = slide.cloneNode(true);
          copy.setAttribute("aria-hidden", "true");
          copy.dataset.clone = "true";
          copy.classList.add("is-clone");
          track.append(copy);
        });
      }

      gallery.append(track);
      gallery.classList.add("is-driven");
      frames = Array.from(track.children);
      /* A set narrower than the window cannot be wrapped without the join
         being on screen, so a product with one or two photographs simply
         does not loop. */
      looping = slides.length > 1;

      /* The header stays solid on a product page.

         It was made transparent over the band on the reasoning that the film is
         the same situation as the homepage hero. It is not: the hero is one
         art-directed image whose tone the merchant sets per slide, while this
         band is whatever photographs a product happens to have, moving sideways
         under the logo. A header that has to survive all of them needs a scrim,
         and a scrim across the top of the photographs is worse than a clean bar
         above them. */
    }

    let hovered = -1;
    let current = -1;
    let job = 0;

    const paint = (index) => {
      if (index === current) return;
      current = index;
      frames.forEach((frame, i) => frame.classList.toggle("is-lit", i === index));
    };

    /* The focus point, and there is only one.

       It is the middle of the part of the band the panel is not standing on —
       the same point the frames snap to. Lighting from the middle of the whole
       band would light a frame the panel is half covering, so the subject and
       the centred photograph would disagree. They read the same number now. */
    const focusPoint = () => {
      const box = gallery.getBoundingClientRect();
      /* Read the reserve off scroll-padding rather than off the custom property
         it came from. A custom property hands back the token it was written as
         — `calc(min(420px, 38vw) + …)` — which is a string, not a length; the
         same value used in a real property has already been resolved to pixels
         by the time it is asked for. */
      const reserve = parseFloat(window.getComputedStyle(gallery).scrollPaddingRight) || 0;
      return box.left + (box.width - reserve) / 2;
    };

    const nearest = () => {
      const mid = focusPoint();
      return frames.reduce((best, frame, i) => {
        const rect = frame.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - mid);
        return distance < best.distance ? { index: i, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    };

    const frameWidth = () => slides[0]?.getBoundingClientRect().width || gallery.clientWidth / 3;
    const setWidth = () => frameWidth() * slides.length;

    /* The wrap. Done on the raw scrollLeft with no smoothing, so it lands in
       the same visual place and the shopper cannot see the join. */
    /* True only while a step's smooth scroll is in flight. The wrap must not
       run then: it writes scrollLeft, writing scrollLeft cancels a smooth
       scroll, and mandatory snapping then pulls the reel back to where it
       started. That is a standstill, and it is exactly what the arrows did —
       every press appeared to do nothing. */
    let stepping = 0;

    const wrap = () => {
      if (!looping || stepping) return;
      const span = setWidth();
      /* Seamless only while one set is wider than the window. */
      if (span <= 0 || span <= gallery.clientWidth) return;
      /* Keep the scroll inside the middle set. A jump of exactly one set lands
         on the identical frame in the neighbouring copy, so there is nothing
         to see. */
      if (gallery.scrollLeft >= span * 2) gallery.scrollLeft -= span;
      else if (gallery.scrollLeft < span) gallery.scrollLeft += span;
    };

    const resolve = () => {
      job = 0;
      wrap();
      if (hovered >= 0) return paint(hovered);
      paint(nearest());
    };

    const schedule = () => {
      if (job) return;
      job = window.requestAnimationFrame(resolve);
    };

    /* An arrow moves the photographs and nothing else. It scrolls the band, not
       the page, so the title, the price and Add to bag stay exactly where the
       shopper left them. */
    const step = (direction) => {
      if (!driven) return;
      window.clearTimeout(stepping);
      gallery.scrollBy({ left: direction * frameWidth(), behavior: reduced ? "auto" : "smooth" });
      /* Let the animation land, then let the wrap correct the position. The
         correction is a whole set, so it moves the scroll without moving the
         picture. */
      stepping = window.setTimeout(() => {
        stepping = 0;
        schedule();
      }, reduced ? 40 : 640);
    };

    if (window.matchMedia("(hover: hover)").matches) {
      slides.forEach((slide, index) => {
        slide.addEventListener("pointerenter", () => { hovered = index; schedule(); });
      });
      gallery.addEventListener("pointerleave", () => { hovered = -1; schedule(); });
    }

    if (driven) {
      const nav = document.createElement("div");
      nav.className = "t-spotlight-nav";
      nav.innerHTML = `
        <button type="button" data-spot-prev aria-label="Previous photograph"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m14.5 5-7 7 7 7"/></svg></button>
        <button type="button" data-spot-next aria-label="Next photograph"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m9.5 5 7 7-7 7"/></svg></button>`;
      nav.querySelector("[data-spot-prev]").addEventListener("click", () => step(-1));
      nav.querySelector("[data-spot-next]").addEventListener("click", () => step(1));
      gallery.parentNode.insertBefore(nav, gallery.nextSibling);

      /* A plain wheel over the band is a vertical gesture and belongs to the
         page — it is left alone. A wheel with a horizontal component, which is
         what a trackpad swipe and shift-and-wheel both produce, is the band's,
         and only that component is taken. */
      gallery.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        gallery.scrollLeft += event.deltaX;
        schedule();
      }, { passive: false });

      window.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        /* A key event on the window targets the window, which is not an
           Element and has no closest(). Real typing always targets a node, but
           a synthetic event does not, and neither does every browser. */
        const from = event.target instanceof Element ? event.target : null;
        if (from?.closest("input, textarea, select, [contenteditable]")) return;
        if (document.querySelector(".t-lightbox")) return;
        const band = gallery.getBoundingClientRect();
        if (band.bottom < 120 || band.top > window.innerHeight - 120) return;
        event.preventDefault();
        step(event.key === "ArrowRight" ? 1 : -1);
      });

      /* Press and drag.

         A scroll container answers a trackpad and a wheel but not a held mouse
         button — on a desktop with a mouse there is no horizontal gesture at
         all, which is why the band felt dead to drag. So the pointer moves it
         directly.

         A drag must not also be a click: the figures open the full view, and
         letting go after moving the reel 300px should not put a photograph on
         the screen. Past a few pixels of movement the gallery is marked as
         dragging and the click that follows is swallowed once. */
      let from = null;
      let fromLeft = 0;
      let moved = 0;

      gallery.addEventListener("pointerdown", (event) => {
        if (event.button !== 0 || event.pointerType === "touch") return;
        from = event.clientX;
        fromLeft = gallery.scrollLeft;
        moved = 0;
        gallery.classList.add("is-grabbing");
      });

      gallery.addEventListener("pointermove", (event) => {
        if (from === null) return;
        const travelled = event.clientX - from;
        if (Math.abs(travelled) > 3) moved = Math.abs(travelled);
        gallery.scrollLeft = fromLeft - travelled;
        schedule();
      });

      const release = () => {
        if (from === null) return;
        from = null;
        gallery.classList.remove("is-grabbing");
        if (moved > 6) {
          gallery.classList.add("is-dragged");
          window.setTimeout(() => gallery.classList.remove("is-dragged"), 0);
        }
      };

      gallery.addEventListener("pointerup", release);
      gallery.addEventListener("pointercancel", release);
      gallery.addEventListener("pointerleave", release);
      /* The browser's own image drag would start instead of ours. */
      gallery.addEventListener("dragstart", (event) => event.preventDefault());
      gallery.addEventListener("click", (event) => {
        if (moved > 6) {
          event.stopPropagation();
          event.preventDefault();
          moved = 0;
        }
      }, true);

      /* Start on the second set so there is reel in both directions from the
         first moment; it looks identical to starting at the beginning. */
      const settle = () => {
        const span = setWidth();
        if (span > 0) gallery.scrollLeft = span;
        schedule();
      };
      if (document.readyState === "complete") settle();
      else window.addEventListener("load", settle, { once: true });
      settle();
    }

    /* How far the section sits from the left edge of the page.

       The band bleeds out to the page edge with a negative inline margin, and
       that margin was hard-coded to the page gutter. That is right only while
       the gutter is the only thing insetting the section — change the wrapper,
       or put the section anywhere else, and the band hangs off the edge or
       falls short of it. Measuring the real distance is exact wherever it
       lands, and unlike `100vw` it does not count the scrollbar. */
    const bleed = () => {
      const host = gallery.closest(".t-product");
      if (!host) return;
      const inset = Math.round(host.getBoundingClientRect().left);
      host.style.setProperty("--film-bleed", `${inset}px`);
    };
    bleed();
    window.addEventListener("resize", bleed, { passive: true });

    gallery.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    wide.addEventListener?.("change", () => schedule());

    schedule();
  }

  /* Pairs well with, moved into the room it was leaving empty.

     The film is a screen tall and pinned; the panel beside it is two or three
     screens of reading. Once the band has travelled as far as its section
     allows it releases, and everything below that point is a column of blank
     page the width of the photographs — a screenful of nothing next to the
     description, which is what the client kept pointing at.

     The complementary rail was already the next thing on the page. It is now
     the thing that fills that column instead of following it. Nothing is
     duplicated and nothing is invented: the same markup, one row lower in the
     same grid.

     It is moved rather than re-rendered, and it moves inside `.t-product`,
     which lives in the same Shopify section wrapper it came from — so the
     theme editor still targets it, and no route-locked Liquid changes.

     Desktop only. On a phone the rail is full width under the product, which is
     correct, and there is no empty column to fill. */
  bindProductPairsInset() {
    const section = document.querySelector(".template-product .t-product--gallery-spotlight");
    if (!section || section.dataset.pairsInset === "true") return;
    /* Prefer the compact rail — the one the phone uses.

       It renders `compact-paired-card`: a thumbnail, the name, the price and a
       real add button that puts a single-variant product straight in the bag
       and opens the size drawer for anything with options. That is the card
       the client asked for, and it already exists; restyling the homepage card
       to imitate it would have produced a worse copy of something the theme
       already renders. The full-width rail is the fallback for a template set
       to the standard mobile layout. */
    const host = section.parentElement;
    const compact = section.querySelector(".t-complementary-mobile");
    const standard = host?.querySelector(":scope > .t-complementary");

    /* Only the compact rail is inset.

       The wide rail was being moved too, and then styled by nothing — its
       cards are the tall homepage kind and every rule written for this column
       is written for the compact card, so it arrived in the sidebar with no
       treatment at all. A template set to the standard mobile layout keeps its
       full-width rail below the product, which is a correct page; a half-styled
       sidebar is not. */
    const pairs = compact;
    if (!pairs) return;
    section.dataset.pairsInset = "true";
    if (compact) section.classList.add("t-product--inset-compact");

    /* Remember where it belongs, so the phone can have it back verbatim. */
    const home = document.createComment("pairs-well-with-home");
    pairs.parentNode.insertBefore(home, pairs);

    const wide = window.matchMedia("(min-width: 750px)");

    const apply = () => {
      if (wide.matches) {
        if (pairs.parentElement === section) return;
        section.appendChild(pairs);
        pairs.classList.add("is-inset");
        section.classList.add("t-product--has-inset-pairs");
        /* The wide rail would otherwise render the same products a second
           time, further down the page. */
        if (compact && standard) standard.classList.add("is-superseded");
      } else {
        if (pairs.parentElement !== section) return;
        home.parentNode.insertBefore(pairs, home.nextSibling);
        pairs.classList.remove("is-inset");
        section.classList.remove("t-product--has-inset-pairs");
        if (compact && standard) standard.classList.remove("is-superseded");
      }
    };

    apply();
    wide.addEventListener?.("change", apply);
  }

  /* Full view.

     A shopper who wants to look closely at a garment should be able to, and the
     only honest place for that is the whole screen. Clicking any product
     photograph opens it at full size, and the same reel is there — arrows, the
     arrow keys, a swipe, or the wheel move through it; Escape or the backdrop
     closes it and returns focus to the photograph that was clicked.

     Built from the photographs already on the page, so it works on every
     gallery layout and adds no markup to the route-locked section. */
  bindProductLightbox() {
    const gallery = document.querySelector(".template-product .t-product__gallery");
    if (!gallery) return;
    /* Clones are excluded from the reel — they are the same photographs, and
       counting them would report "3 / 16" on a product with eight. They still
       open it, further down, at the index of the original they copy. */
    const figures = Array.from(gallery.querySelectorAll("figure:not(.is-clone)"));
    const shots = figures
      .map((figure) => {
        const img = figure.querySelector("img");
        if (!img) return null;
        /* The largest candidate the browser knows about, so the full view is
           actually bigger than the tile rather than the same file upscaled. */
        const largest = (img.getAttribute("srcset") || "")
          .split(",")
          .map((part) => part.trim().split(/\s+/))
          .filter((part) => part.length === 2 && part[1].endsWith("w"))
          .sort((a, b) => parseInt(b[1], 10) - parseInt(a[1], 10))[0];
        return { src: largest ? largest[0] : img.currentSrc || img.src, alt: img.alt || "", figure };
      })
      .filter(Boolean);
    if (!shots.length) return;

    let box = null;
    let index = 0;
    let opener = null;

    /* Magnification.

       Opening a photograph at the size of the screen is not zooming in — on a
       phone a fitted portrait shot is barely larger than the one in the
       gallery, which is why the full view read as doing nothing. A shopper
       opens it to see the knit, the print, the stitching. So the picture
       magnifies: a tap or a click takes it to 2.6x about the point that was
       touched, dragging moves it around underneath, and a pinch sets the scale
       directly. Tapping again puts it back. */
    let scale = 1;
    let panX = 0;
    let panY = 0;
    const MAX = 2.6;

    const paint = () => {
      const img = box.querySelector("[data-lightbox-image]");
      if (!img) return;
      /* Never let the picture be dragged off the screen entirely: the pan is
         bounded by however much of it is actually hidden at this scale.

         Measured from the layout box, not getBoundingClientRect — that returns
         the box *after* the transform, so multiplying it by the scale again
         compounded it and the bound grew with every zoom. */
      const slackX = Math.max(0, (img.offsetWidth * scale - window.innerWidth) / 2);
      const slackY = Math.max(0, (img.offsetHeight * scale - window.innerHeight) / 2);
      panX = Math.min(slackX, Math.max(-slackX, panX));
      panY = Math.min(slackY, Math.max(-slackY, panY));
      img.style.transform = `translate3d(${panX.toFixed(1)}px, ${panY.toFixed(1)}px, 0) scale(${scale})`;
      box.classList.toggle("is-zoomed", scale > 1.01);
    };

    const resetZoom = () => {
      scale = 1;
      panX = 0;
      panY = 0;
      paint();
    };

    /* Zoom about the point that was pressed, so the detail under the finger is
       the detail that grows — anchoring to the centre instead makes the shopper
       hunt for what they were looking at. */
    const zoomAt = (clientX, clientY) => {
      const img = box.querySelector("[data-lightbox-image]");
      if (!img) return;
      if (scale > 1.01) return resetZoom();
      const frame = img.getBoundingClientRect();
      const fromCentreX = clientX - (frame.left + frame.width / 2);
      const fromCentreY = clientY - (frame.top + frame.height / 2);
      scale = MAX;
      panX = -fromCentreX * (MAX - 1);
      panY = -fromCentreY * (MAX - 1);
      paint();
    };

    const render = () => {
      const shot = shots[index];
      const img = box.querySelector("[data-lightbox-image]");
      img.src = shot.src;
      img.alt = shot.alt;
      /* A new photograph arrives at its own size, never inheriting the last
         one's magnification. */
      resetZoom();
      box.querySelector("[data-lightbox-count]").textContent = `${index + 1} / ${shots.length}`;
      box.querySelectorAll("[data-lightbox-dot]").forEach((dot, i) => {
        dot.classList.toggle("is-active", i === index);
        dot.setAttribute("aria-current", i === index ? "true" : "false");
      });
    };

    const move = (step) => {
      index = (index + step + shots.length) % shots.length;
      render();
    };

    const close = () => {
      if (!box) return;
      box.classList.remove("is-open");
      document.documentElement.classList.remove("t-lightbox-open");
      window.setTimeout(() => {
        box?.remove();
        box = null;
      }, 220);
      opener?.focus?.();
    };

    const build = () => {
      box = document.createElement("div");
      box.className = "t-lightbox";
      box.setAttribute("role", "dialog");
      box.setAttribute("aria-modal", "true");
      box.setAttribute("aria-label", "Product photographs");
      box.innerHTML = `
        <button class="t-lightbox__scrim" type="button" data-lightbox-close aria-label="Close full view"></button>
        <figure class="t-lightbox__stage"><img data-lightbox-image src="" alt=""></figure>
        <button class="t-lightbox__close" type="button" data-lightbox-close aria-label="Close full view"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
        <button class="t-lightbox__step t-lightbox__step--prev" type="button" data-lightbox-prev aria-label="Previous photograph"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m14.5 5-7 7 7 7"/></svg></button>
        <button class="t-lightbox__step t-lightbox__step--next" type="button" data-lightbox-next aria-label="Next photograph"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m9.5 5 7 7-7 7"/></svg></button>
        <div class="t-lightbox__rail">
          <p class="t-lightbox__count" data-lightbox-count aria-live="polite"></p>
          <div class="t-lightbox__dots">${shots.map((_, i) => `<button class="t-lightbox__dot" type="button" data-lightbox-dot aria-label="Photograph ${i + 1}"></button>`).join("")}</div>
        </div>`;
      document.body.appendChild(box);

      box.querySelectorAll("[data-lightbox-close]").forEach((el) => el.addEventListener("click", close));
      box.querySelector("[data-lightbox-prev]").addEventListener("click", () => move(-1));
      box.querySelector("[data-lightbox-next]").addEventListener("click", () => move(1));
      box.querySelectorAll("[data-lightbox-dot]").forEach((dot, i) => {
        dot.addEventListener("click", () => {
          index = i;
          render();
        });
      });

      box.addEventListener("keydown", (event) => {
        if (event.key === "Escape") close();
        else if (event.key === "ArrowRight") move(1);
        else if (event.key === "ArrowLeft") move(-1);
        else if (event.key === "Tab") {
          /* A dialog keeps the keyboard inside it. */
          const focusable = box.querySelectorAll("button");
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });

      /* The wheel does whichever job the picture is currently in the middle of:
         magnify while it is magnified, step through the reel while it is not. */
      let wheelAt = 0;
      box.addEventListener("wheel", (event) => {
        event.preventDefault();
        if (scale > 1.01) {
          scale = Math.min(MAX * 1.6, Math.max(1, scale - event.deltaY * 0.002));
          if (scale <= 1.01) resetZoom();
          else paint();
          return;
        }
        const now = event.timeStamp;
        if (now - wheelAt < 260) return;
        if (Math.abs(event.deltaY) < 12 && Math.abs(event.deltaX) < 12) return;
        wheelAt = now;
        move(event.deltaY + event.deltaX > 0 ? 1 : -1);
      }, { passive: false });

      /* A press on the picture magnifies it; a press and a move drags it. The
         two are told apart by distance, so a drag never leaves the shopper
         zoomed somewhere they did not ask to be. */
      const stage = box.querySelector(".t-lightbox__stage");
      let grabX = 0;
      let grabY = 0;
      let grabbing = false;
      let travelled = 0;

      stage.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "touch") return;
        grabbing = true;
        travelled = 0;
        grabX = event.clientX - panX;
        grabY = event.clientY - panY;
        stage.setPointerCapture?.(event.pointerId);
      });

      stage.addEventListener("pointermove", (event) => {
        if (!grabbing) return;
        travelled += Math.abs(event.movementX) + Math.abs(event.movementY);
        if (scale <= 1.01) return;
        panX = event.clientX - grabX;
        panY = event.clientY - grabY;
        paint();
      });

      stage.addEventListener("pointerup", (event) => {
        if (!grabbing) return;
        grabbing = false;
        if (travelled < 6) zoomAt(event.clientX, event.clientY);
      });

      stage.addEventListener("dblclick", (event) => event.preventDefault());

      /* Touch: one finger swipes the reel or drags a magnified picture, two
         fingers set the scale directly. */
      let swipeFrom = null;
      let pinchFrom = 0;
      let pinchScale = 1;
      const spread = (touches) =>
        Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

      box.addEventListener("touchstart", (event) => {
        if (event.touches.length === 2) {
          pinchFrom = spread(event.touches);
          pinchScale = scale;
          swipeFrom = null;
          return;
        }
        swipeFrom = event.touches[0]?.clientX ?? null;
        grabX = (event.touches[0]?.clientX ?? 0) - panX;
        grabY = (event.touches[0]?.clientY ?? 0) - panY;
        travelled = 0;
      }, { passive: true });

      box.addEventListener("touchmove", (event) => {
        if (event.touches.length === 2 && pinchFrom > 0) {
          event.preventDefault();
          scale = Math.min(MAX * 1.6, Math.max(1, pinchScale * (spread(event.touches) / pinchFrom)));
          paint();
          return;
        }
        if (scale > 1.01 && event.touches.length === 1) {
          event.preventDefault();
          panX = event.touches[0].clientX - grabX;
          panY = event.touches[0].clientY - grabY;
          travelled += 10;
          paint();
        }
      }, { passive: false });

      box.addEventListener("touchend", (event) => {
        if (pinchFrom > 0 && event.touches.length < 2) {
          pinchFrom = 0;
          if (scale <= 1.01) resetZoom();
          return;
        }
        if (swipeFrom === null) return;
        const delta = (event.changedTouches[0]?.clientX ?? swipeFrom) - swipeFrom;
        /* While magnified the finger is panning, not paging. */
        if (scale <= 1.01) {
          if (Math.abs(delta) > 45) move(delta < 0 ? 1 : -1);
          else if (travelled < 6 && event.target.closest(".t-lightbox__stage")) {
            const touch = event.changedTouches[0];
            zoomAt(touch.clientX, touch.clientY);
          }
        }
        swipeFrom = null;
      }, { passive: true });
    };

    const open = (at, from) => {
      index = at;
      opener = from;
      build();
      render();
      document.documentElement.classList.add("t-lightbox-open");
      window.requestAnimationFrame(() => {
        box.classList.add("is-open");
        box.querySelector("[data-lightbox-next]")?.focus();
      });
    };

    /* Every figure on screen opens the full view, clones included — a shopper
       cannot tell which is which and should not have to. A clone opens at the
       original it copies, so the count always reads against the real reel. */
    Array.from(gallery.querySelectorAll("figure")).forEach((figure, position) => {
      /* Per figure, not per gallery. The gallery is rebuilt when the width
         crosses the breakpoint, which destroys the clones and makes new ones;
         binding per figure means the new ones get zoom and the surviving
         originals do not get a second handler. */
      if (figure.dataset.zoomBound === "true") return;
      figure.dataset.zoomBound = "true";
      figure.classList.add("t-zoomable");
      figure.addEventListener("click", (event) => {
        /* Never swallow a real control — video controls and 3D model buttons
           live inside these figures. */
        if (event.target.closest("a, button, video, model-viewer, iframe")) return;
        open(position % shots.length, event.currentTarget);
      });
    });
  }

  /* Put the gallery back exactly as Liquid rendered it.

     Both galleries build real DOM — the carousel wraps everything in a shell
     and adds controls and a thumbnail rail; the film moves the figures into a
     track and clones them twice. Neither can simply be re-run over the other's
     leftovers, so this undoes both down to the original figures in their
     original order, and lets the next bind start from nothing. */
  resetProductGallery(gallery) {
    const pristine = gallery.tactPristine;
    if (!pristine) return;

    /* Anything either gallery built. */
    gallery.parentNode
      ?.querySelectorAll(".t-spotlight-nav, .t-product-gallery__controls, .t-product-gallery__thumbs, .t-product-gallery__navigation")
      .forEach((el) => el.remove());

    /* The carousel wraps the gallery in a shell; lift it back out. */
    const shell = gallery.closest(".t-product-gallery-shell");
    if (shell && shell.parentNode) {
      shell.parentNode.insertBefore(gallery, shell);
      shell.remove();
    }

    /* Clones are the film's; the originals go back as direct children. */
    gallery.querySelectorAll("figure.is-clone").forEach((el) => el.remove());
    pristine.forEach((figure) => {
      figure.classList.remove("is-active", "is-lit", "is-clone");
      figure.removeAttribute("style");
      figure.removeAttribute("aria-hidden");
      gallery.appendChild(figure);
    });
    gallery.querySelector(".t-spotlight-track")?.remove();

    gallery.className = "t-product__gallery";
    gallery.removeAttribute("style");
    gallery.scrollLeft = 0;
    delete gallery.dataset.galleryBound;

    /* The header's transparent state belongs to the film. */
    document.querySelector(".t-header")?.classList.remove("t-header--overlay", "t-header--film");
  }

  /* Nike-style product media stage: full media + thumbnail rail + prev/next +
     counter + swipe + keyboard. Restored in 1.9.0 — the v1.8.0 build dropped
     this method body while keeping the call, which threw in the constructor and
     killed every binding after it (cart drawer, quick view, wishlist, reveals). */
  bindProductMediaGallery() {
    const gallery = document.querySelector(".template-product .t-product__gallery");
    if (!gallery || gallery.dataset.galleryBound === "true") return;
    const slides = Array.from(gallery.querySelectorAll(":scope > figure"));
    if (slides.length < 2) return;

    /* Spotlight and mosaic show every photograph at once — that is the whole
       point of them — so the single-slide carousel must not claim the gallery.
       They get bindProductGalleryFocus() instead, which only decides which
       photograph is lit. The carousel stays the behaviour for "single".

       On a phone none of that applies. A band of three shots side by side is a
       desktop composition; the phone had a carousel — one photograph, a
       thumbnail rail, arrows, a counter, a swipe — and it was right. These
       layouts are desktop layouts, so below 750px the carousel keeps the
       gallery exactly as it was before any of this work. */
    const layout = gallery.closest("[data-gallery-layout]")?.dataset.galleryLayout || "single";
    const breakpoint = window.matchMedia("(min-width: 750px)");

    /* Which gallery is correct depends on the width, and the width can change
       after the page has loaded — a rotated phone, a dragged window, a device
       toolbar being opened. Binding once at load meant a desktop-width load
       that was then narrowed kept the desktop film, thumbnails and all, and no
       amount of resizing brought the phone's carousel back.

       So the pristine markup is remembered here, once, and crossing the
       breakpoint puts the gallery back to it and binds the right one again. */
    if (!gallery.dataset.galleryWatched) {
      gallery.dataset.galleryWatched = "true";
      gallery.tactPristine = slides.slice();
      breakpoint.addEventListener?.("change", () => {
        this.resetProductGallery(gallery);
        this.bindProductMediaGallery();
        this.bindProductLightbox();
      });
    }

    if (breakpoint.matches && (layout === "spotlight" || layout === "mosaic")) {
      this.bindProductGalleryFocus(gallery, slides, layout);
      return;
    }

    /* One gallery on every screen.

       Desktop used to be a tall vertical stack the page scrolled through. The
       client's objection is the right one: a shopper had to scroll to see the
       piece they had already chosen to look at, and the buy panel drifted away
       while they did. The phone's carousel shows the whole garment at once and
       is changed with a swipe. There is no reason the desktop deserves worse,
       and one gallery is also one set of bugs instead of two. */
    gallery.dataset.galleryBound = "true";
    gallery.classList.add("is-enhanced");

    const controls = document.createElement("div");
    controls.className = "t-product-gallery__controls";
    controls.innerHTML = `
      <button type="button" data-product-gallery-prev aria-label="Previous product media"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 5-7 7 7 7"/></svg></button>
      <p aria-live="polite"><b data-product-gallery-current>1</b> / ${slides.length}</p>
      <button type="button" data-product-gallery-next aria-label="Next product media"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 5 7 7-7 7"/></svg></button>
    `;
    const thumbnails = document.createElement("div");
    thumbnails.className = "t-product-gallery__thumbs";
    thumbnails.setAttribute("aria-label", "Choose product media");

    slides.forEach((slide, index) => {
      slide.dataset.galleryIndex = String(index);
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.productGalleryThumb = String(index);
      button.setAttribute("aria-label", `Show product media ${index + 1}`);
      const preview = slide.querySelector("img")?.cloneNode(true);
      if (preview) {
        preview.removeAttribute("srcset");
        preview.removeAttribute("sizes");
        preview.loading = "lazy";
        button.appendChild(preview);
      } else {
        button.innerHTML = `<span>${index + 1}</span>`;
      }
      thumbnails.appendChild(button);
    });
    const shell = document.createElement("div");
    shell.className = "t-product-gallery-shell";
    gallery.parentNode.insertBefore(shell, gallery);
    const navigation = document.createElement("div");
    navigation.className = "t-product-gallery__navigation";
    navigation.append(thumbnails, controls);
    shell.append(gallery, navigation);

    const current = controls.querySelector("[data-product-gallery-current]");
    const thumbButtons = Array.from(thumbnails.querySelectorAll("button"));
    let active = 0;
    const show = (index, behavior = "smooth") => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === active;
        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
        slide.setAttribute("aria-hidden", String(!isActive));
      });
      if (current) current.textContent = String(active + 1);
      thumbButtons.forEach((button, buttonIndex) => {
        button.classList.toggle("is-active", buttonIndex === active);
        if (buttonIndex === active) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });
      const activeThumb = thumbButtons[active];
      if (activeThumb) {
        const left = activeThumb.offsetLeft - (thumbnails.clientWidth - activeThumb.clientWidth) / 2;
        thumbnails.scrollTo({ left: Math.max(0, left), behavior });
      }
      slides.forEach((slide, slideIndex) => {
        if (slideIndex !== active) slide.querySelectorAll("video").forEach((video) => video.pause());
      });
      if (this.motionOk) {
        const shown = slides[active];
        if (shown) this.M.animate(shown, { opacity: [0.4, 1], scale: [1.01, 1] }, this.spring.soft);
      }
    };
    controls.querySelector("[data-product-gallery-prev]")?.addEventListener("click", () => show(active - 1));
    controls.querySelector("[data-product-gallery-next]")?.addEventListener("click", () => show(active + 1));
    thumbButtons.forEach((button, index) => button.addEventListener("click", () => show(index)));
    gallery.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") show(active - 1);
      if (event.key === "ArrowRight") show(active + 1);
    });
    /* Drag to change the picture.

       The old version listened for pointerdown and pointerup and nothing else,
       which worked under a finger and almost never under a mouse: pressing on
       an <img> and moving starts the browser's own image drag, the pointer is
       taken by that gesture, and the pointerup never arrives. So the gallery
       answered arrows and thumbnails and appeared to ignore the mouse
       entirely. Three things fix it — refuse the native drag, capture the
       pointer so every move belongs to us, and move the picture while the hand
       is moving so the drag is visibly a drag rather than a dead press. */
    let swipeStartX = null;
    let swipeStartAt = 0;
    let swipeDelta = 0;
    let swiping = false;

    gallery.addEventListener("dragstart", (event) => event.preventDefault());

    const settle = () => {
      /* Every slide, not just the current one. A drag that changes the picture
         moves `active` on before this runs, so clearing only `slides[active]`
         left the figure that was actually dragged holding its offset — it
         looked correct until the shopper came back to it, and then that one
         photograph sat a few dozen pixels off centre for the rest of the
         visit. */
      slides.forEach((slide) => {
        slide.style.transition = "";
        slide.style.transform = "";
      });
      gallery.classList.remove("is-dragging");
      swipeStartX = null;
      swipeDelta = 0;
      swiping = false;
    };

    gallery.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      /* A second finger landing mid-drag would otherwise re-anchor the gesture
         and make the picture jump. */
      if (swiping) return;
      swipeStartX = event.clientX;
      swipeStartAt = event.timeStamp;
      swipeDelta = 0;
      swiping = true;
      gallery.classList.add("is-dragging");
      /* Capture is an optimisation, not a requirement: it keeps the drag alive
         when the pointer leaves the gallery. It throws NotFoundError if the
         pointer is already gone, and an exception here would abort the rest of
         the handler and leave dragging dead. */
      try {
        gallery.setPointerCapture?.(event.pointerId);
      } catch (_) {}
    });

    gallery.addEventListener("pointermove", (event) => {
      if (!swiping || swipeStartX === null) return;
      swipeDelta = event.clientX - swipeStartX;
      const shown = slides[active];
      /* Damped, so the picture leans with the hand without sliding off. */
      if (shown) shown.style.transform = `translate3d(${swipeDelta * 0.32}px, 0, 0)`;
    });

    const release = (event) => {
      if (!swiping) return;
      const distance = swipeDelta;
      const shown = slides[active];
      if (shown) shown.style.transition = "transform 0.3s var(--tact-ease-standard)";
      gallery.classList.remove("is-dragging");
      swiping = false;
      swipeStartX = null;

      /* Distance or speed, whichever the hand offered. Requiring 45px meant a
         quick flick — which is how people actually page through photographs —
         did nothing, and the picture sprang back as though the gesture had
         been rejected. A fast, short movement is just as clear an instruction
         as a slow, long one. */
      const elapsed = Math.max(1, (event?.timeStamp || performance.now()) - swipeStartAt);
      const velocity = Math.abs(distance) / elapsed;
      if (Math.abs(distance) >= 45 || (velocity > 0.35 && Math.abs(distance) > 12)) {
        show(active + (distance < 0 ? 1 : -1));
      }
      /* Whether it changed or sprang back, the inline transform has to go, or
         the next picture inherits the last drag's offset. */
      window.setTimeout(settle, 320);
    };

    gallery.addEventListener("pointerup", release);
    gallery.addEventListener("pointercancel", settle);
    gallery.addEventListener("lostpointercapture", release);
    gallery.tabIndex = 0;
    show(0, "auto");
  }

  /* Bonkers-style buy column: the images scroll, and the decision block —
     title, price, colour, size, add to bag — stays pinned while the rest of the
     panel (offers, value points, tabs, pairs-well-with) scrolls past it.
     Pinning the whole column does nothing once it is taller than the viewport,
     which is why it never appeared to stick. Wrapping in JS keeps the
     route-locked section untouched. */
  bindProductBuybox() {
    const detail = document.querySelector(".template-product .t-product__detail");
    if (!detail || detail.dataset.buyboxBound === "true") return;
    if (window.matchMedia("(max-width: 749px)").matches) return;
    /* Honour the merchant's own setting. "Keep product details visible on
       desktop" being off is documented as "one continuous page scroll", but
       this method built a sticky sub-block regardless — an opaque, z-indexed
       box that the description and the delivery and returns highlights then
       slid underneath as the page moved. That was the overlap: a second sticky
       context nobody asked for, inside a column the merchant had set to
       scroll normally. */
    /* Spotlight is the exception, and deliberately so.

       "Keep product details visible on desktop" is off by default, and it means
       something specific: whether the whole panel is pinned as one. In this
       layout the photographs are already held still while the shopper reads,
       and the decision — title, price, size, Add to bag — is the one thing that
       must not slide away underneath them. Pinning it is not a preference here,
       it is what the layout is. The setting still governs the panel as a whole,
       and every other gallery layout obeys it exactly as before. */
    const isFilm = !!detail.closest(".t-product--gallery-spotlight");
    if (!isFilm && detail.closest(".t-product--static-details")) return;
    const anchor = detail.querySelector(".t-product__buy-row")
      || detail.querySelector(".t-product__buy")
      || detail.querySelector(".t-product-form");
    if (!anchor) return;
    detail.dataset.buyboxBound = "true";

    const children = Array.from(detail.children);
    const stopIndex = children.findIndex((child) => child === anchor || child.contains(anchor));
    if (stopIndex < 0) return;

    const box = document.createElement("div");
    box.className = "t-product__buybox";
    detail.insertBefore(box, children[0]);
    box.append(...children.slice(0, stopIndex + 1));
  }

  /* Moves the wishlist button next to Buy it now so the product title keeps its
     full width. Restored in 1.9.0 alongside bindProductMediaGallery. */
  bindProductPurchaseLayout() {
    const detail = document.querySelector(".template-product .t-product__detail");
    if (!detail || detail.dataset.purchaseLayoutBound === "true") return;
    detail.dataset.purchaseLayoutBound = "true";
    const wishlist = detail.querySelector(".t-product__wishlist");
    const buy = detail.querySelector(".t-product__buy");
    if (!wishlist || !buy) return;
    const row = document.createElement("div");
    row.className = "t-product__buy-row";
    buy.parentNode.insertBefore(row, buy);
    row.append(buy, wishlist);
    detail.classList.add("is-purchase-enhanced");
  }

  /* Card add pill: press feedback + a spring hand-off to the drawer. The hover
     entrance itself stays in CSS so it works with no JS. */
  bindProductCardMotion() {
    if (this.cardMotionBound) return;
    this.cardMotionBound = true;
    document.addEventListener("pointerdown", (event) => {
      const pill = event.target.closest(".tqa-trigger");
      if (pill) {
        this.press(pill, 0.9);
        return;
      }
      const button = event.target.closest(
        ".t-btn, .t-button, .qv__add, .t-product__add, .t-cart-drawer__checkout, .t-shop-split__cta, .t-hero-slide__copy a, .t-arrow-btn, .t-look-sheet__item",
      );
      if (button && !button.disabled) this.press(button, 0.97);
    });
    document.addEventListener("click", (event) => {
      const pill = event.target.closest("[data-quick-view-open].tqa-trigger");
      if (!pill) return;
      this.quickViewOrigin = pill;
      this.animateSpring(pill, { scale: [1, 0.82], opacity: [1, 0] }, "snap", { duration: 0.22 });
    });
  }

  restoreQuickViewOrigin() {
    const pill = this.quickViewOrigin;
    this.quickViewOrigin = null;
    if (!pill) return;
    if (this.motionOk) this.M.animate(pill, { scale: [0.82, 1], opacity: [0, 1] }, this.spring.snap);
    else {
      pill.style.opacity = "";
      pill.style.transform = "";
    }
  }

  /* Pointer-drag + dot indicators for the media rail inside the quick-view
     drawer. The rail is re-rendered on every open, so this runs per open.
     From 900px the drawer is two columns and the images stack vertically in
     their own scroller, so there is nothing to wire — drag and dots are the
     phone rail only. A horizontal rail is the reason the images could not be
     changed with a mouse. */
  bindQuickGalleries(scope) {
    if (!window.matchMedia("(max-width: 899px)").matches) return;
    const rails = (scope || document).querySelectorAll("[data-quick-view-media]:not([data-rail-bound])");
    rails.forEach((rail) => {
      rail.dataset.railBound = "true";
      const shots = rail.querySelectorAll(".qv__shot");
      if (shots.length < 2) return;

      let down = false;
      let startX = 0;
      let startScroll = 0;
      rail.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        /* Only when this rail actually scrolls sideways.

           On desktop the quick-add drawer stacks its photographs and scrolls
           them vertically, and this handler was still capturing the pointer on
           mousedown to drag a `scrollLeft` that cannot move. Holding the
           pointer to perform a gesture the element cannot perform is why the
           drawer's images would not scroll: the drag won the pointer and then
           did nothing with it. */
        if (rail.scrollWidth <= rail.clientWidth + 1) return;
        down = true;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        try {
          rail.setPointerCapture?.(event.pointerId);
        } catch (_) {}
        rail.classList.add("is-dragging");
      });
      rail.addEventListener("pointermove", (event) => {
        if (!down) return;
        rail.scrollLeft = startScroll - (event.clientX - startX);
      });
      /* Index from the shots themselves, not from the rail width. A shot is
         narrower than the rail (the next one peeks), so scrollLeft/clientWidth
         under-counted: with seven shots the last one reported as the fifth,
         and the dots said there was still more to come. */
      const nearestShot = () => {
        let best = 0;
        let bestDistance = Infinity;
        shots.forEach((shot, index) => {
          const distance = Math.abs(shot.offsetLeft - rail.scrollLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = index;
          }
        });
        return best;
      };
      const scrollToShot = (index, smooth = true) => {
        const shot = shots[Math.max(0, Math.min(shots.length - 1, index))];
        if (!shot) return;
        rail.scrollTo({ left: shot.offsetLeft, behavior: smooth && this.motionOk ? "smooth" : "auto" });
      };

      const release = () => {
        if (!down) return;
        down = false;
        rail.classList.remove("is-dragging");
        scrollToShot(nearestShot());
      };
      rail.addEventListener("pointerup", release);
      rail.addEventListener("pointercancel", release);

      const dots = document.createElement("div");
      dots.className = "qv__dots";
      shots.forEach((_, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("aria-label", `View image ${index + 1}`);
        dot.addEventListener("click", () => scrollToShot(index));
        dots.append(dot);
      });
      rail.after(dots);
      const sync = () => {
        /* At the very end the rail cannot scroll further, so the last shot's
           offset is never reached exactly — pin the final dot explicitly. */
        const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
        const index = atEnd ? shots.length - 1 : nearestShot();
        dots.querySelectorAll("button").forEach((dot, dotIndex) => {
          dot.classList.toggle("is-active", dotIndex === index);
        });
      };
      rail.addEventListener("scroll", () => window.requestAnimationFrame(sync), { passive: true });
      sync();
    });
  }

  /* Sticky purchase bar on the product page. Built entirely from the existing
     buy panel DOM so the route-locked section is never touched. */
  bindProductStickyBar() {
    if (this.stickyBarBound || !document.body.classList.contains("template-product")) return;
    const panel = document.querySelector(".t-product__detail");
    const purchase = document.querySelector(".t-product__purchase");
    const form = document.querySelector(".t-product-form");
    const addButton = form?.querySelector('button[name="add"]');
    const price = document.querySelector("[data-product-price]");
    if (!panel || !purchase || !form || !addButton || !("IntersectionObserver" in window)) return;
    this.stickyBarBound = true;

    const bar = document.createElement("div");
    bar.className = "t-product__stickybar";
    bar.hidden = true;
    bar.innerHTML =
      '<span class="t-product__stickybar-meta">' +
      '<b data-sticky-title></b>' +
      '<i data-sticky-size></i>' +
      "</span>" +
      '<span class="t-product__stickybar-price" data-sticky-price></span>' +
      '<button type="button" class="t-product__stickybar-add"><span>' +
      (addButton.textContent.trim() || "Add to bag") +
      "</span></button>";
    document.body.append(bar);

    const titleEl = bar.querySelector("[data-sticky-title]");
    const sizeEl = bar.querySelector("[data-sticky-size]");
    const priceEl = bar.querySelector("[data-sticky-price]");
    const proxyAdd = bar.querySelector(".t-product__stickybar-add");
    titleEl.textContent = document.querySelector(".t-product__heading h1")?.textContent?.trim() || "";

    const sync = () => {
      priceEl.innerHTML = price ? price.innerHTML : "";
      /* The rails are per option now, so the bar reads the chosen values —
         one for a tee, "M / L" for a set. */
      const picked = [...form.querySelectorAll("[data-option] input[data-option-input]:checked")]
        .map((input) => input.value.trim())
        .filter((value) => value && value !== "Default Title");
      sizeEl.textContent = picked.join(" / ");
      const disabled = addButton.disabled;
      proxyAdd.disabled = disabled;
      proxyAdd.querySelector("span").textContent = disabled ? "Sold out" : (addButton.textContent.trim() || "Add to bag");
    };
    form.addEventListener("change", sync);
    document.addEventListener("tact:cart-updated", sync);
    sync();

    proxyAdd.addEventListener("click", () => {
      this.press(proxyAdd, 0.96);
      if (typeof form.requestSubmit === "function") form.requestSubmit(addButton);
      else addButton.click();
    });

    let live = false;
    const show = (visible) => {
      if (visible === live) return;
      live = visible;
      if (visible) bar.hidden = false;
      bar.classList.toggle("is-live", visible);
      if (this.motionOk) {
        /* JS owns the transform; the .is-live class is just a state hook. */
        this.M.animate(bar, { y: visible ? ["110%", "0%"] : "110%" }, visible ? this.spring.sheet : { ...this.spring.sheet, stiffness: 460 });
      }
      if (!visible) window.setTimeout(() => { if (!live) bar.hidden = true; }, 420);
    };

    const observer = new IntersectionObserver(
      ([entry]) => show(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0, rootMargin: "-4px 0px 0px 0px" },
    );
    observer.observe(purchase);

    /* IntersectionObserver's initial callback covers the already-scrolled case
       (deep-link, refresh mid-page). A scroll listener is not needed. */
  }

  bindRevealMotion() {
    /* Reveals used a spring, and a spring overshoots — every section and card
       arrived with a small bounce, all at once, which is what reads as cheap.
       A reveal is not a physical object being thrown; it is something coming
       into focus. So: one long decelerating ease, no overshoot, and only the
       cards move. Whole sections just fade, because sliding a full-width block
       18px makes the page heave.

       Stagger runs across a row rather than the document, so a grid resolves
       left to right instead of four unrelated things twitching together. */
    const sections = document.querySelectorAll("#MainContent > section:not(.t-hero-carousel)");
    const items = document.querySelectorAll("#MainContent [data-product-card], #MainContent .t-collection-tile, .t-footer__support > a");
    const targets = [...sections, ...items];
    if (!targets.length) return;

    targets.forEach((target) => { target.dataset.reveal = "true"; });

    if (this.reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach((target) => target.classList.add("is-revealed"));
      return;
    }

    document.documentElement.classList.add("t-motion-ready");

    /* Position within its own row, so cards in a grid cascade. */
    const rowIndex = (el) => {
      const parent = el.parentElement;
      if (!parent) return 0;
      const siblings = [...parent.children].filter((c) => c.dataset.reveal === "true");
      const top = Math.round(el.getBoundingClientRect().top);
      const sameRow = siblings.filter((c) => Math.abs(Math.round(c.getBoundingClientRect().top) - top) < 8);
      return Math.max(0, sameRow.indexOf(el));
    };

    const EASE = [0.16, 1, 0.3, 1];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          observer.unobserve(target);
          target.classList.add("is-revealed");
          if (!this.motionOk) return;

          const isSection = target.matches("#MainContent > section");
          if (isSection) {
            /* A section only fades. Nothing the width of the page should move. */
            this.M.animate(target, { opacity: [0, 1] }, { duration: 0.55, easing: EASE });
            return;
          }
          this.M.animate(
            target,
            { opacity: [0, 1], transform: ["translateY(12px)", "translateY(0px)"] },
            { duration: 0.7, easing: EASE, delay: Math.min(rowIndex(target), 5) * 0.06 },
          );
        });
      },
      { rootMargin: "0px 0px -7% 0px", threshold: 0.08 },
    );
    targets.forEach((target) => observer.observe(target));
  }

  bindThemeMode() {
    const root = document.documentElement;
    if (root.dataset.themeEnabled !== "true") return;

    const storageKey = "tact-theme";
    const system = window.matchMedia("(prefers-color-scheme: dark)");
    const readStored = () => {
      try {
        return window.localStorage.getItem(storageKey);
      } catch {
        return null;
      }
    };
    const writeStored = (value) => {
      try {
        window.localStorage.setItem(storageKey, value);
      } catch {}
    };
    const apply = (theme) => {
      root.dataset.theme = theme;
      root.style.colorScheme = theme;
      const pageColor = getComputedStyle(root)
        .getPropertyValue("--tact-paper")
        .trim();
      document.querySelector('meta[name="theme-color"]')?.setAttribute(
        "content",
        pageColor || (theme === "dark" ? "#0c0c0d" : "#f3f3f1"),
      );
      document.querySelectorAll("[data-theme-toggle]").forEach((button) => {
        const next = theme === "dark" ? "light" : "dark";
        button.setAttribute("aria-label", `Use ${next} mode`);
        const label = button.querySelector("[data-theme-toggle-label]");
        if (label) label.textContent = `${next[0].toUpperCase()}${next.slice(1)} mode`;
      });
      window.dispatchEvent(
        new CustomEvent("tact:theme-changed", { detail: { theme } }),
      );
    };
    const stored = readStored();
    const preferred =
      root.dataset.themeDefault === "system"
        ? (system.matches ? "dark" : "light")
        : root.dataset.themeDefault;

    apply(stored || preferred || "light");
    document.querySelectorAll("[data-theme-toggle]").forEach((button) => {
      if (button.dataset.themeBound === "true") return;
      button.dataset.themeBound = "true";
      button.addEventListener("click", () => {
        const next = root.dataset.theme === "dark" ? "light" : "dark";
        writeStored(next);
        apply(next);
      });
    });
    system.addEventListener?.("change", (event) => {
      if (!readStored()) {
        apply(event.matches ? "dark" : "light");
      }
    });
  }

  openMenu() {
    this.closeCart();
    this.closeWishlist();
    this.header?.classList.remove("is-hidden");
    this.menu?.classList.add("is-open");
    this.menu?.setAttribute("aria-hidden", "false");
    this.overlay?.classList.add("is-open");
    this.animateDrawer(this.menu, this.overlay, true, { axis: "y", closed: "-102%", preset: "snap" });
    this.lockPageScroll();
  }

  closeMenu() {
    if (!this.menu) return;
    if (!this.menu.classList.contains("is-open")) return;
    this.menu.classList.remove("is-open");
    this.menu.setAttribute("aria-hidden", "true");
    this.overlay?.classList.remove("is-open");
    this.animateDrawer(this.menu, this.overlay, false, { axis: "y", closed: "-102%", preset: "snap" });
    this.unlockPageScroll();
  }

  openSizeGuide(part) {
    /* The size guide panel is rendered by the product section, so it does not
       exist on a collection page — where the quick-add drawer also offers a
       Sizing guide button. This used to lock the page scroll regardless, which
       froze the storefront behind a panel that was never there. Re-query rather
       than trust the boot-time reference, and never lock without a panel. */
    this.sizeGuide = this.sizeGuide || document.querySelector("[data-size-guide]");
    if (!this.sizeGuide) return;
    this.sizeOverlay = this.sizeOverlay || document.querySelector(".t-size-overlay");
    this.sizeGuide.classList.add("is-open");
    this.sizeGuide.setAttribute("aria-hidden", "false");
    this.sizeOverlay?.classList.add("is-open");
    this.sizeGuide.querySelectorAll("[data-chart-tab]").forEach((tab) => {
      if (tab.dataset.tabBound === "true") return;
      tab.dataset.tabBound = "true";
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        this.showChartTab(tab);
      });
    });
    this.selectChartPart(this.sizeGuide, part);
    this.lockPageScroll();
  }

  /* A set has a chart per garment. Opening the guide from the "Shirt size" rail
     should land on the shirt's table, not on whichever tab happens to be first,
     so each tab carries the words that name its piece and the option's own name
     decides. */
  selectChartPart(root, part) {
    if (!root || !part) return;
    const tabs = [...root.querySelectorAll("[data-chart-tab]")];
    if (tabs.length < 2) return;
    const name = ` ${String(part).toLowerCase().replace(/[-/]/g, " ")} `;
    const hit = tabs.find((tab) =>
      (tab.dataset.partMatch || "")
        .toLowerCase()
        .split(",")
        .map((word) => word.trim())
        .filter(Boolean)
        .some((word) => name.includes(` ${word} `)),
    );
    if (hit) this.showChartTab(hit);
  }

  showChartTab(tab) {
    const chart = tab.closest("[data-size-chart]");
    if (!chart) return;
    chart.querySelectorAll("[data-chart-tab]").forEach((button) => {
      const on = button === tab;
      button.classList.toggle("is-active", on);
      button.setAttribute("aria-selected", on ? "true" : "false");
    });
    chart.querySelectorAll("[data-chart-panel]").forEach((panel) => {
      const on = panel.dataset.chartPanel === tab.dataset.chartTab;
      panel.classList.toggle("is-active", on);
      panel.hidden = !on;
    });
  }

  /* Fills the shared panel with a product's own size guide, pulled from that
     product's section so the measurements never diverge from the product page.
     Used by the quick-add drawer, which has no product section of its own. */
  async openSharedSizeGuide(productUrl, title) {
    const panel = document.querySelector("[data-size-guide-shared]");
    const body = document.querySelector("[data-size-guide-shared-body]");
    if (!panel || !body || !productUrl) return false;

    const heading = document.querySelector("[data-size-guide-shared-title]");
    if (heading && title) heading.textContent = title;

    if (body.dataset.loadedFor !== productUrl) {
      body.innerHTML = '<p class="t-size-guide__loading">Loading size guide…</p>';

      /* The Section Rendering API keys off the section's id in the template,
         not its type. templates/product.json names it "main"; asking for
         "main-product" returned nothing every time, which is why this always
         fell through to "not available". Both are tried, so a template that
         names it differently still works. */
      const fetchChart = async (sectionId) => {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set("section_id", sectionId);
        const response = await fetch(url.toString(), { headers: { Accept: "text/html" } });
        if (!response.ok) return null;
        const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
        return (
          parsed.querySelector("[data-size-guide] .t-size-guide__body") ||
          parsed.querySelector("[data-size-chart]") ||
          null
        );
      };

      try {
        let chart = null;
        for (const sectionId of ["main", "main-product"]) {
          chart = await fetchChart(sectionId);
          if (chart) break;
        }
        if (!chart) throw new Error("no chart in section");
        body.innerHTML = chart.innerHTML;
        body.dataset.loadedFor = productUrl;
      } catch (_) {
        /* A bare "not available" is a dead end. Offer the product page, which
           always has the chart, so the drawer is never a wall. */
        const safeUrl = this.safeStoreUrl ? this.safeStoreUrl(productUrl) : productUrl;
        body.innerHTML =
          '<p class="t-size-guide__loading">We could not load the chart for this piece here.</p>' +
          '<p class="t-size-guide__loading"><a class="t-btn" href="' + safeUrl + '">Open the product page</a></p>';
      }
    }

    panel.hidden = false;
    document.querySelector("[data-size-guide-shared-overlay]")?.removeAttribute("hidden");
    /* Next frame, so the panel has a laid-out starting position to slide from. */
    window.requestAnimationFrame(() => {
      panel.classList.add("is-open");
      panel.setAttribute("aria-hidden", "false");
      document.querySelector("[data-size-guide-shared-overlay]")?.classList.add("is-open");
    });
    this.lockPageScroll();
    return true;
  }

  closeSizeGuide() {
    this.sizeGuide?.classList.remove("is-open");
    this.sizeGuide?.setAttribute("aria-hidden", "true");
    this.sizeOverlay?.classList.remove("is-open");

    const shared = document.querySelector("[data-size-guide-shared]");
    const sharedOverlay = document.querySelector("[data-size-guide-shared-overlay]");
    if (shared) {
      shared.classList.remove("is-open");
      shared.setAttribute("aria-hidden", "true");
      sharedOverlay?.classList.remove("is-open");
      /* hidden only after the slide out, so the panel does not vanish mid-move. */
      window.setTimeout(() => {
        if (shared.classList.contains("is-open")) return;
        shared.hidden = true;
        sharedOverlay?.setAttribute("hidden", "");
      }, this.motionOk ? 420 : 200);
    }
    this.unlockPageScroll();
  }

  openWishlist() {
    const drawer = this.wishlist || document.querySelector("[data-wishlist-drawer]");
    /* Flag this drawer open before closing the one being replaced.
       anyOverlayOpen() answers from the DOM, so a close that runs while
       nothing is flagged open sees an empty room and lifts the scroll lock —
       leaving the page free to move behind the drawer that is about to
       appear, and throwing away the position it was holding. */
    drawer?.classList.add("is-open");
    drawer?.setAttribute("aria-hidden", "false");
    this.closeCart();
    this.closeMenu();
    this.header?.classList.remove("is-hidden");
    document.querySelector(".t-wishlist-overlay")?.classList.add("is-open");
    this.mobileDock?.querySelector("[data-wishlist-open]")?.classList.add("is-active");
    this.mobileDock?.querySelector("[data-wishlist-open]")?.setAttribute("aria-pressed", "true");
    this.lockPageScroll();
  }

  closeWishlist() {
    const drawer = this.wishlist || document.querySelector("[data-wishlist-drawer]");
    if (!drawer?.classList.contains("is-open")) return;
    drawer.classList.remove("is-open");
    drawer?.setAttribute("aria-hidden", "true");
    document.querySelector(".t-wishlist-overlay")?.classList.remove("is-open");
    this.mobileDock?.querySelector("[data-wishlist-open]")?.classList.remove("is-active");
    this.mobileDock?.querySelector("[data-wishlist-open]")?.setAttribute("aria-pressed", "false");
    this.unlockPageScroll();
  }

  /* --------------------------------------------------------------------------
     Variant options.

     The picker is one rail per option, so the variant has to be resolved from
     the combination rather than read off a radio. That is what lets a co-ord be
     sold as one product with a size for the shirt and a size for the shorts —
     the Chapter 2 pattern — instead of thirty-six radios reading "XS / S".
     -------------------------------------------------------------------------- */
  bindVariantOptions() {
    document.querySelectorAll("[data-variant-picker]").forEach((picker) => {
      if (picker.dataset.variantBound === "true") return;
      picker.dataset.variantBound = "true";

      const scope = picker.closest("form") || picker.parentElement;
      const idInput = scope?.querySelector("[data-variant-id]");
      const dataNode = scope?.querySelector("[data-variant-data]");
      if (!idInput || !dataNode) return;

      let variants = [];
      try {
        variants = JSON.parse(dataNode.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      const groups = [...picker.querySelectorAll("[data-option]")];
      if (!groups.length) return;

      const addButton = scope.querySelector('button[name="add"]');
      const addLabel = addButton?.dataset.addLabel || "Add to bag";
      const priceNode = document.querySelector("[data-product-price]");

      const chosen = () =>
        groups.map((group) => group.querySelector("input[data-option-input]:checked")?.value ?? null);

      const fits = (variant, values) =>
        values.every((value, index) => value === null || variant.options[index] === value);

      /* A value is dimmed when no available variant uses it alongside the other
         current choices — but never disabled. On a set the shopper may want to
         change the shirt first and the shorts second, and a disabled radio makes
         that impossible. */
      const paint = (values) => {
        groups.forEach((group, index) => {
          group.querySelectorAll("input[data-option-input]").forEach((input) => {
            const trial = values.slice();
            trial[index] = input.value;
            const possible = variants.some((variant) => variant.available && fits(variant, trial));
            input.closest("label")?.classList.toggle("is-sold-out", !possible);
          });
        });
      };

      const apply = (silent) => {
        const values = chosen();
        paint(values);

        const variant = variants.find((candidate) => fits(candidate, values));
        if (!variant) {
          if (addButton) {
            addButton.disabled = true;
            const label = addButton.querySelector("span");
            if (label) label.textContent = "Unavailable";
          }
          return;
        }

        idInput.value = variant.id;

        if (priceNode) {
          const nodes = [];
          if (variant.compare_text) {
            const was = document.createElement("s");
            was.textContent = variant.compare_text;
            nodes.push(was);
          }
          const now = document.createElement("strong");
          now.textContent = variant.price_text || "";
          nodes.push(now);
          priceNode.replaceChildren(...nodes);
        }

        if (addButton) {
          addButton.disabled = !variant.available;
          const label = addButton.querySelector("span");
          if (label) label.textContent = variant.available ? addLabel : "Sold out";
        }

        if (!silent) {
          /* Keep the address bar honest so a shared link opens on the same
             combination, without adding a history entry per tap. */
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          window.history.replaceState({}, "", url);
          document.dispatchEvent(
            new CustomEvent("tact:variant-changed", { detail: { variant, values } }),
          );
        }
      };

      picker.addEventListener("change", (event) => {
        if (!event.target.matches("input[data-option-input]")) return;
        apply(false);
      });

      apply(true);
    });
  }

  /* --------------------------------------------------------------------------
     Header category drops. Everything inside them comes from the Shopify
     navigation, so this only decides when a panel is on screen.
     -------------------------------------------------------------------------- */
  bindNavDrops() {
    const items = [...document.querySelectorAll("[data-nav-item]")].filter((item) =>
      item.querySelector("[data-nav-drop]"),
    );
    if (!items.length) return;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        const drop = item.querySelector("[data-nav-drop]");
        if (!drop || drop.hidden) return;
        item.classList.remove("is-open");
        item.querySelector("[data-nav-trigger]")?.setAttribute("aria-expanded", "false");
        window.clearTimeout(Number(drop.dataset.hideTimer));
        drop.dataset.hideTimer = window.setTimeout(() => {
          drop.hidden = true;
        }, 200);
      });
    };

    const open = (item) => {
      const drop = item.querySelector("[data-nav-drop]");
      if (!drop) return;
      closeAll(item);
      window.clearTimeout(Number(drop.dataset.hideTimer));
      drop.hidden = false;
      /* One frame between "in the DOM" and "opening", so the transition has a
         starting state to move from. */
      window.requestAnimationFrame(() => {
        item.classList.add("is-open");
        item.querySelector("[data-nav-trigger]")?.setAttribute("aria-expanded", "true");
      });
    };

    items.forEach((item) => {
      if (item.dataset.navBound === "true") return;
      item.dataset.navBound = "true";

      let hover = null;
      item.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "touch") return;
        window.clearTimeout(hover);
        hover = window.setTimeout(() => open(item), 60);
      });
      item.addEventListener("pointerleave", (event) => {
        if (event.pointerType === "touch") return;
        window.clearTimeout(hover);
        hover = window.setTimeout(() => closeAll(null), 140);
      });
      item.addEventListener("focusin", () => open(item));
      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget)) closeAll(null);
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeAll(null);
    });
    this.closeNavDrops = () => closeAll(null);
  }

  /* The drawer's category lists are an accordion on a phone, where the whole
     menu would otherwise run to several screens, and open columns on a desktop,
     where there is room to show every category at once. */
  bindMenuCategories() {
    const entries = [...document.querySelectorAll("[data-menu-entry]")];
    if (!entries.length) return;

    /* Closed at rest, on every size. Opening all of them turned the drawer
       into a wall of category pills — the four things a visitor is actually
       choosing between were buried under twelve they were not. The categories
       are one tap away here, and on a desktop they are already in the header's
       hover panel. */
    entries.forEach((entry) => {
      entry.open = false;
      entry.addEventListener("toggle", () => {
        if (!entry.open) return;
        entries.forEach((other) => {
          if (other !== entry) other.open = false;
        });
      });
    });
  }

  updateVariant(input) {
    const price = document.querySelector("[data-product-price]");
    if (!price) return;
    const compare = input.dataset.compare;
    const nodes = [];
    if (compare) {
      const original = document.createElement("s");
      original.textContent = compare;
      nodes.push(original);
    }
    const current = document.createElement("strong");
    current.textContent = input.dataset.price || "";
    nodes.push(current);
    price.replaceChildren(...nodes);
  }

  changeQuantity(button, amount) {
    const purchase = button.closest(".t-product__quantity");
    const input = purchase?.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = Math.max(1, Number(input.value || 1) + amount);
  }

  changeProductTab(button) {
    const information = button.closest(".t-product__information");
    if (!information) return;
    const key = button.dataset.productTab;
    information.querySelectorAll("[data-product-tab]").forEach((tab) => {
      tab.classList.toggle("is-active", tab === button);
    });
    information.querySelectorAll("[data-product-panel]").forEach((panel) => {
      const active = panel.dataset.productPanel === key;
      panel.classList.toggle("is-active", active);
      if (active && this.motionOk) {
        this.M.animate(panel, { opacity: [0, 1], y: [8, 0] }, this.spring.soft);
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const theme = new TactTheme();
  document.addEventListener("shopify:section:load", () => {
    theme.bindStoryVideos();
    theme.bindCategoryDecks();
    theme.bindCollectionFilters();
    theme.bindHeroCarousels();
    theme.bindHeroVideos();
    theme.bindReviewStories();
    theme.bindVideoQuickAdd();
    theme.bindProductForms();
    theme.bindProductMediaGallery();
    theme.bindProductPurchaseLayout();
    theme.bindProductBuybox();
    theme.bindCartDrawer();
    theme.bindDragRails();
    theme.bindWishlist();
    theme.bindProductCardMotion();
    theme.bindQuickGalleries();
    theme.bindProductStickyBar();
    theme.bindThemeMode();
    theme.bindMobileDock();
  });
});
